import { useState, useEffect, useCallback } from 'react';
import config from '../config';

const API_URL = config.API_URL;

// Variable globale pour stocker temporairement l'ID du compte actif
let currentActiveAccountId = 'global';

// ===== API SYNC POUR PRÉFÉRENCES MÉTRIQUES =====

// Token d'authentification (sera set par initMetricsSync)
let authToken = null;

// Flag pour savoir si on a déjà synchronisé
let hasSyncedToApi = false;

/**
 * Initialise la synchronisation des préférences métriques
 * Appelé au démarrage de l'app avec le token utilisateur
 */
export const initMetricsSync = async (token) => {
  if (!token) {
    console.log('⚠️ [METRICS-SYNC] Pas de token, mode localStorage uniquement');
    return;
  }

  authToken = token;

  try {
    // 1. Charger les préférences depuis l'API
    const response = await fetch(`${API_URL}/api/metrics/preferences`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (!response.ok) {
      console.error('❌ [METRICS-SYNC] Erreur chargement API:', response.status);
      return;
    }

    const result = await response.json();

    if (result.success) {
      const apiData = result.data;

      // 2. Si l'API a des données, les utiliser comme source de vérité
      if (apiData.metricsConfig && Object.keys(apiData.metricsConfig).length > 0) {
        console.log('✅ [METRICS-SYNC] Préférences chargées depuis l\'API');

        // Mettre à jour le localStorage avec les données API
        localStorage.setItem('kokpeet_component_metrics', JSON.stringify(apiData.metricsConfig));

        /**
         * Les préférences du serveur viennent d'écraser le stockage local : il faut leur
         * appliquer les migrations, sans quoi une configuration enregistrée avant une
         * évolution reviendrait intacte à chaque chargement. Et la renvoyer aussitôt, sinon
         * la même correction serait à refaire à la connexion suivante, indéfiniment.
         */
        if (migrateAdsMetrics(true)) {
          const migre = localStorage.getItem('kokpeet_component_metrics');
          if (migre) saveToApi(JSON.parse(migre));
        }

        if (apiData.globalLabels && Object.keys(apiData.globalLabels).length > 0) {
          localStorage.setItem('kokpeet_global_labels', JSON.stringify(apiData.globalLabels));
        }

        if (apiData.objectives && Object.keys(apiData.objectives).length > 0) {
          localStorage.setItem('kokpeet_metric_objectives', JSON.stringify(apiData.objectives));
          invalidateObjectivesCache();
        }

        hasSyncedToApi = true;

        // Notifier les composants du changement
        window.dispatchEvent(new Event('componentMetricsChanged'));

      } else {
        // 3. Si l'API est vide, synchroniser les données localStorage vers l'API
        console.log('🔄 [METRICS-SYNC] API vide, migration localStorage → API');
        await syncLocalStorageToApi(token);
      }
    }
  } catch (error) {
    console.error('❌ [METRICS-SYNC] Erreur initialisation:', error);
  }
};

/**
 * Synchronise les préférences localStorage vers l'API (migration one-time)
 */
const syncLocalStorageToApi = async (token) => {
  if (hasSyncedToApi) return;

  try {
    const metricsConfig = JSON.parse(localStorage.getItem('kokpeet_component_metrics') || '{}');
    const globalLabels = JSON.parse(localStorage.getItem('kokpeet_global_labels') || '{}');
    const objectives = JSON.parse(localStorage.getItem('kokpeet_metric_objectives') || '{}');

    // Si rien à synchroniser, skip. Le marqueur de version ne compte pas pour un réglage :
    // sans cette réserve, une configuration vide mais estampillée passerait pour renseignée.
    if (Object.keys(metricsConfig).filter(k => k !== CLE_VERSION).length === 0) {
      console.log('ℹ️ [METRICS-SYNC] Rien à synchroniser');
      hasSyncedToApi = true;
      return;
    }

    const response = await fetch(`${API_URL}/api/metrics/preferences/sync`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ metricsConfig, globalLabels, objectives })
    });

    if (response.ok) {
      const result = await response.json();
      if (result.synced) {
        console.log('✅ [METRICS-SYNC] Préférences migrées vers l\'API');
      } else {
        console.log('ℹ️ [METRICS-SYNC] ' + result.message);
      }
      hasSyncedToApi = true;
    }
  } catch (error) {
    console.error('❌ [METRICS-SYNC] Erreur sync:', error);
  }
};

/**
 * Sauvegarde les préférences vers l'API en arrière-plan
 * Ne bloque pas - le localStorage reste la source de vérité immédiate
 */
const saveToApi = async (metricsConfig = null, globalLabels = null, objectives = null) => {
  if (!authToken) return;

  try {
    const body = {};
    if (metricsConfig !== null) body.metricsConfig = metricsConfig;
    if (globalLabels !== null) body.globalLabels = globalLabels;
    if (objectives !== null) body.objectives = objectives;

    const response = await fetch(`${API_URL}/api/metrics/preferences`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(body)
    });

    /*
     * **Un refus du serveur ne doit pas passer pour un enregistrement.**
     *
     * Le code de réponse n'était pas lu : un 4xx ou un 5xx se déroulait sans un mot. Or le
     * serveur est la source de vérité — `initMetricsSync` écrase le stockage local avec ce
     * qu'il rend au chargement suivant. Un enregistrement refusé se traduisait donc par un
     * réglage qui « ne se mémorise pas », sans la moindre trace pour le dire.
     */
    if (!response.ok) {
      const detail = await response.text().catch(() => '');
      console.error(
        `❌ [METRICS-API] Le serveur a refusé l'enregistrement (${response.status}). `
        + 'Le réglage tiendra jusqu\'au prochain chargement, puis sera écrasé par la version '
        + `du serveur. ${detail.slice(0, 200)}`
      );
    }

  } catch (error) {
    console.error('⚠️ [METRICS-API] Erreur sauvegarde API (localStorage OK):', error);
  }
};

// ===== FIN API SYNC =====

// Version des métriques - incrémenter pour forcer une migration
// 12 : arrivée d'Awin, dont les colonnes `clics` et `taux_conv` doivent rejoindre les
// réglages déjà enregistrés. Sans cette incrémentation, la migration ne serait jamais rejouée
// chez ceux qui sont passés par la version précédente — et le graphe, qui ne trace qu'une
// métrique cochée, garderait silencieusement une autre courbe.
// 13 : la même correction pour le tableau des éditeurs, resté sur ses colonnes d'origine.
// 14 : les deux précédentes ne s'appliquaient pas à une configuration sans champ `order`.
// 15 : le dashboard Awin s'ouvre désormais sur le template E-commerce.
// 16 : le tableau des campagnes Awin, qui n'existait pas et n'avait donc aucun réglage.
const METRICS_VERSION = 16;

/**
 * Clé du marqueur de version, rangée dans les préférences elles-mêmes.
 *
 * Deux soulignés en tête pour qu'elle ne puisse pas entrer en collision avec un identifiant
 * de compte, qui est toujours un cuid ou « global ».
 */
const CLE_VERSION = '__metricsVersion';

// Migration automatique des préférences Microsoft Ads, Facebook Ads et GA4
// Cette fonction FORCE le bon ordre pour tous les composants
const migrateAdsMetrics = (forcer = false) => {
  try {
    const versionKey = 'kokpeet_metrics_version';
    const savedVersion = localStorage.getItem(versionKey);

    console.log(`🔍 [MIGRATION] Version actuelle: ${savedVersion}, Version cible: ${METRICS_VERSION}`);

    /**
     * `forcer` sert après une synchronisation depuis l'API.
     *
     * Les préférences du serveur écrasent le stockage local à chaque chargement : une
     * migration faite au démarrage était donc défaite quelques instants plus tard, et le
     * symptôme — des colonnes qui ne changent jamais — ne désignait rien de ce qui le
     * causait. La migration doit donc se rejouer **sur les données qui viennent d'arriver**.
     */
    if (!forcer && savedVersion && parseInt(savedVersion) >= METRICS_VERSION) {
      console.log('✅ [MIGRATION] Déjà à jour');
      return false;
    }

    const saved = localStorage.getItem('kokpeet_component_metrics');
    if (!saved) {
      console.log('ℹ️ [MIGRATION] Pas de préférences sauvegardées');
      localStorage.setItem(versionKey, String(METRICS_VERSION));
      return false;
    }

    const parsed = JSON.parse(saved);

    /**
     * La version voyage **avec les préférences**, et pas seulement dans le stockage local.
     *
     * Sans ce marqueur, `forcer` rejouait la migration à **chaque** chargement, puisque les
     * données arrivent du serveur et que le compteur local, lui, était déjà à jour. Or cette
     * migration ne migre pas : elle réécrit l'ordre des colonnes et rajoute `roas` et `roi`
     * partout. Elle défaisait donc, à chaque ouverture, tout choix de l'utilisateur —
     * template appliqué, colonnes réordonnées à la main, métrique décochée.
     *
     * Le compteur local reste en place pour le premier passage, celui qui s'exécute au
     * chargement du module avant toute synchronisation.
     */
    if (parsed[CLE_VERSION] && parseInt(parsed[CLE_VERSION], 10) >= METRICS_VERSION) {
      console.log('✅ [MIGRATION] Préférences déjà migrées (marqueur embarqué)');
      localStorage.setItem(versionKey, String(METRICS_VERSION));
      return false;
    }

    let modified = false;

    /**
     * Les ordres canoniques des quatre régies ont été retirés d'ici avec les normalisations
     * qu'ils servaient. Ils restent déclarés ailleurs — `COMPONENT_AVAILABLE_METRICS` porte
     * l'ordre dans lequel les métriques sont proposées, ce qui est leur seul emploi légitime.
     */

    /**
     * Bon ordre pour Awin.
     *
     * Ni impressions ni CTR : Awin ne mesure l'impression que sur les emplacements display,
     * absents de l'affiliation. L'ordre suit l'entonnoir — le trafic, ce qu'il convertit, ce
     * qu'il coûte, ce qu'il rapporte.
     */
    const awinCorrectOrder = ['clics', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'panier_moyen', 'valeur_conv', 'cos'];
    const awinComponents = ['awin_results', 'awin_publishers', 'awin_campaigns'];

    // Ordre spécifique pour ga4_weekdays avec transactions et conversions séparées
    const ga4WeekdaysCorrectOrder = ['visites', 'visiteurs', 'rebond', 'pages_vues', 'pages_visite', 'duree_moy', 'transactions', 'chiffre_affaires', 'taux_transaction', 'panier_moyen', 'conv', 'taux_conv'];

    // Parcourir tous les comptes
    Object.keys(parsed).forEach(accountId => {
      // Le marqueur de version cohabite avec les comptes dans le même objet : le traiter
      // comme un compte ferait chercher des composants dans une chaîne de caractères.
      if (accountId === CLE_VERSION) return;
      if (!parsed[accountId] || typeof parsed[accountId] !== 'object') return;

      // Fonction helper pour migrer un composant
      const migrateComponent = (compId, correctOrder, metricsToAdd = ['roas', 'roi']) => {
        if (parsed[accountId][compId]) {
          const comp = parsed[accountId][compId];
          const oldOrder = comp.order ? [...comp.order] : [];
          const oldMetrics = comp.metrics ? [...comp.metrics] : [];

          if (comp.order && comp.order.length > 0) {
            // D'abord, ajouter les nouvelles métriques si pas présentes
            let currentMetrics = [...comp.order];
            metricsToAdd.forEach(metric => {
              if (correctOrder.includes(metric) && !currentMetrics.includes(metric)) {
                currentMetrics.push(metric);
                console.log(`➕ [MIGRATION] Ajout de ${metric} pour ${compId} (compte: ${accountId})`);
              }
            });

            // Ensuite, réordonner selon l'ordre correct
            const newOrder = correctOrder.filter(m => currentMetrics.includes(m));
            currentMetrics.forEach(m => {
              if (!newOrder.includes(m)) {
                newOrder.push(m);
              }
            });

            if (JSON.stringify(oldOrder) !== JSON.stringify(newOrder) || JSON.stringify(oldMetrics) !== JSON.stringify(newOrder)) {
              comp.order = newOrder;
              comp.metrics = newOrder;
              modified = true;
              console.log(`🔄 [MIGRATION] Métriques mises à jour pour ${compId} (compte: ${accountId}): ${oldOrder.join(',')} → ${newOrder.join(',')}`);
            }
          }
        }
      };

      /**
       * Migrer les composants Awin.
       *
       * `clics` et `taux_conv` sont arrivés après coup. Sans cette étape, un réglage déjà
       * enregistré ne les gagnerait jamais — et le graphe, qui ne peut tracer qu'une métrique
       * cochée, retomberait silencieusement sur une autre courbe que celle attendue.
       */
      awinComponents.forEach(compId => migrateComponent(compId, awinCorrectOrder, ['clics', 'taux_conv']));

      /**
       * Awin : remettre colonnes **et** graphe à leur réglage courant, sans condition.
       *
       * `migrateComponent` ne fait rien quand la configuration n'a pas de champ `order`, et
       * n'ajoute que les métriques nommées. Deux tentatives de correction par ce chemin ont
       * laissé les colonnes inchangées — d'où cette réécriture franche.
       *
       * Sans risque d'écraser un choix de l'utilisateur : ces réglages ont été créés pendant
       * la mise au point du connecteur, avant qu'il ne soit utilisable. Le jour où Awin aura
       * vécu, ce bloc devra disparaître plutôt que d'être reconduit.
       */
      const defautsAwin = {
        awin_results: ['clics', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'panier_moyen', 'valeur_conv', 'cos'],
        awin_publishers: ['clics', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'panier_moyen', 'valeur_conv', 'cos'],
        awin_campaigns: ['cout', 'conv', 'cpa', 'panier_moyen', 'valeur_conv', 'cos']
      };
      Object.entries(defautsAwin).forEach(([compId, colonnes]) => {
        const comp = parsed[accountId][compId];
        if (!comp) return;
        if (JSON.stringify(comp.metrics) === JSON.stringify(colonnes)
            && JSON.stringify(comp.order) === JSON.stringify(colonnes)) return;

        comp.metrics = [...colonnes];
        comp.order = [...colonnes];
        modified = true;
        console.log(`🔄 [MIGRATION] Colonnes d'${compId} remises au réglage courant : ${colonnes.join(', ')}`);
      });

      /**
       * Et remettre le graphe d'Awin à son réglage courant.
       *
       * `migrateComponent` ne touche que les colonnes. Or un `chartConfig` enregistré **prime**
       * sur le défaut (voir `useComponentMetrics`), si bien qu'un réglage posé pendant la mise
       * au point du connecteur survivait à tout changement ultérieur : les colonnes migraient,
       * le graphe non, et il gardait une courbe que plus personne n'avait demandée.
       *
       * Sans risque d'écraser un choix délibéré : ce réglage-là n'a jamais été proposé à
       * l'utilisateur, il datait du développement.
       */
      if (parsed[accountId].awin_results?.chartConfig) {
        // Valeur écrite en clair, et non lue dans `COMPONENT_DEFAULT_CHART_CONFIG` : cette
        // constante est déclarée bien plus bas dans le fichier, et la migration s'exécute au
        // chargement du module — la référence lèverait une erreur avant même l'affichage.
        parsed[accountId].awin_results.chartConfig = { barMetric: 'conversions', lineMetric: 'clicks' };
        modified = true;
        console.log('🔄 [MIGRATION] Graphe Awin remis au réglage courant (ventes en barres, clics en courbe)');
      }

      /**
       * Les quatre normalisations de régie ont été retirées.
       *
       * `msadsComponents`, `fbadsComponents`, `ga4Components` et `googleAdsComponents`
       * passaient tous par `migrateComponent(compId, ordreCanonique)` — appel qui, sous son
       * nom de migration, **réécrivait l'ordre des colonnes et ajoutait `roas` et `roi`** à
       * toute configuration enregistrée.
       *
       * Ce n'était pas une migration mais un normalisateur permanent, et il défaisait les
       * choix de l'utilisateur : un template appliqué revenait à l'ordre canonique avec deux
       * métriques qu'il excluait, des colonnes réordonnées à la main reprenaient leur place,
       * une métrique décochée réapparaissait. Le symptôme se lisait « les templates ne sont
       * pas mémorisés », et il touchait les quatre régies, pas seulement Meta.
       *
       * Ce qui reste ci-dessus est nommé et borné : les défauts Awin, écrits pendant la mise
       * au point du connecteur, et l'ordre propre à `ga4_weekdays`. Une métrique nouvellement
       * disponible n'est plus ajoutée d'office à une configuration existante — elle se coche
       * dans la modale, ce qui est bien le rôle de celle-ci.
       */
      migrateComponent('ga4_weekdays', ga4WeekdaysCorrectOrder);
    });

    /**
     * Le marqueur est posé même quand rien n'a changé : c'est lui qui dit « ces préférences
     * ont vu cette version », et c'est ce qui empêche la réécriture de se rejouer au
     * chargement suivant. Ne l'écrire qu'en cas de modification laisserait le cas le plus
     * fréquent — une configuration déjà conforme — repasser indéfiniment par la migration.
     */
    parsed[CLE_VERSION] = METRICS_VERSION;
    localStorage.setItem('kokpeet_component_metrics', JSON.stringify(parsed));
    console.log(modified
      ? '✅ [MIGRATION] Préférences migrées vers le nouvel ordre'
      : 'ℹ️ [MIGRATION] Aucune modification nécessaire, marqueur posé');
    modified = true;

    // Marquer la migration comme effectuée
    localStorage.setItem(versionKey, String(METRICS_VERSION));
    return modified;
  } catch (e) {
    console.error('❌ [MIGRATION] Erreur:', e);
    return false;
  }
};

// Exécuter la migration au chargement du module
migrateAdsMetrics();

// Seuil COS par défaut (en pourcentage)
export const DEFAULT_COS_THRESHOLD = 50;

// Seuil CPA par défaut (en euros)
export const DEFAULT_CPA_THRESHOLD = 50;

// Métriques par défaut disponibles dans l'application
export const DEFAULT_METRICS = [
  { key: 'impressions', label: 'Impressions', selected: true },
  { key: 'views', label: 'Vues', selected: true },
  { key: 'ctr', label: 'CTR', selected: true },
  { key: 'vtr', label: 'Taux de vue', selected: true },
  { key: 'clics', label: 'Clics', selected: true },
  { key: 'cpc', label: 'CPC', selected: true },
  { key: 'cpv', label: 'CPV', selected: true },
  { key: 'cpm', label: 'CPM', selected: true },
  { key: 'engagements', label: 'Engagements', selected: true },
  { key: 'cout', label: 'Coût', selected: true },
  { key: 'taux_engagement', label: 'Taux engagement', selected: true },
  { key: 'taux_conv', label: 'Taux conv.', selected: true },
  { key: 'conv', label: 'Conv.', selected: true },
  { key: 'cpa', label: 'CPA', selected: true },
  { key: 'valeur_conv', label: 'Valeur Conv.', selected: true },
  /**
   * Les achats, distincts des conversions.
   *
   * Décochée par défaut : sur un compte dont les conversions configurées *sont* les achats,
   * les deux colonnes afficheraient le même nombre côte à côte. Celui qui veut comparer les
   * deux la coche.
   */
  { key: 'transactions', label: 'Transactions', selected: false },
  { key: 'cos', label: 'COS', selected: true },
  { key: 'panier_moyen', label: 'Panier moyen', selected: false },
  { key: 'roas', label: 'ROAS', selected: false },
  { key: 'roi', label: 'ROI', selected: false },
  { key: 'sessions', label: 'Sessions', selected: false },
  // Métriques Facebook Ads (désactivées par défaut)
  /**
   * Couverture et répétition, les deux métriques de **personnes** de Meta.
   *
   * ⚠️ Elles ne s'additionnent pas. La couverture est un nombre de comptes touchés : sommer
   * celles de plusieurs campagnes compte deux fois les personnes qui ont vu les deux, et le
   * total dépasse celui de l'interface Meta. La répétition est un ratio (impressions/couverture),
   * qui ne se somme ni ne se moyenne.
   *
   * Seul Meta sait déduplier. Les écrans affichent donc la valeur par ligne — juste, chacune
   * étant dédupliquée dans son propre périmètre — et prennent le **total au serveur**, qui le
   * demande à Meta pour la période entière. Quand ce total n'a pas de sens pour ce qui est
   * affiché (une sélection partielle, par exemple), la case reste vide plutôt que de mentir.
   */
  { key: 'couverture', label: 'Couverture', selected: false },
  { key: 'repetition', label: 'Répétition', selected: false },
  { key: 'interactions', label: 'Interactions', selected: false },
  { key: 'cout_par_interaction', label: 'Coût par interaction', selected: false },
  { key: 'reactions', label: 'Réactions', selected: false },
  { key: 'cout_par_reaction', label: 'Coût par réaction', selected: false },
  /**
   * Vidéo, côté Meta.
   *
   * `videos_vues` compte les **ThruPlay** — quinze secondes de lecture, ou la vidéo entière
   * si elle est plus courte. C'est la définition que Meta retient aujourd'hui pour une vue,
   * et la seule que l'API renvoie sur ces écrans : les lectures de trois secondes ne sont
   * pas demandées.
   *
   * `taux_videos_vues` rapporte ces vues aux impressions — la part des diffusions qui ont
   * donné lieu à une vue, comme le CTR rapporte les clics aux impressions. Ce n'est **pas**
   * un taux de complétion : celui-ci se lirait sur les paliers 25/50/75/100 %, que ces
   * tableaux n'affichent pas.
   *
   * Désactivées par défaut : un compte sans vidéo n'a rien à en faire.
   */
  { key: 'videos_vues', label: 'Vidéos vues', selected: false },
  { key: 'taux_videos_vues', label: '% vidéos vues', selected: false },
  /**
   * Interactions sociales d'une publication Meta.
   *
   * `jaime_facebook` compte les nouveaux **fans de la page**, et non le pouce levé sur la
   * publication : celui-ci est une réaction, déjà comptée par `reactions`. Les confondre
   * gonflerait les fans de tout l'engagement d'un post viral. Le libellé nomme la plateforme
   * parce que la métrique lui est propre : Instagram a des abonnés, pas de fans de page, et
   * l'API n'expose rien d'équivalent pour ses placements.
   *
   * Désactivées par défaut, comme les autres métriques sociales.
   */
  { key: 'commentaires', label: 'Commentaires', selected: false },
  { key: 'enregistrements', label: 'Enregistrements', selected: false },
  { key: 'partages', label: 'Partages', selected: false },
  { key: 'jaime_facebook', label: "J'aime (Facebook)", selected: false }
];

// Mapping entre les clés et les noms de colonnes dans les données
export const METRIC_COLUMN_MAPPING = {
  'impressions': 'impressions',
  'views': 'views',
  'ctr': 'ctr',
  'vtr': 'vtr',
  'clics': 'clics',
  'cpc': 'cpc',
  'cpv': 'cpv',
  'cpm': 'cpm',
  'engagements': 'engagements',
  'cout': 'cout',
  'taux_engagement': 'taux_engagement',
  'taux_conv': 'taux_conversion',
  'conv': 'conversions',
  'cpa': 'cpa',
  'valeur_conv': 'valeur_conversions',
  'cos': 'cos',
  'panier_moyen': 'panier_moyen',
  // Métriques Facebook Ads
  'interactions': 'interactions',
  'cout_par_interaction': 'cout_par_interaction',
  'reactions': 'reactions',
  'cout_par_reaction': 'cout_par_reaction'
};

// Récupère les métriques sélectionnées par l'utilisateur pour un compte spécifique
export const getSelectedMetrics = (accountId = 'global') => {
  try {
    const savedMetrics = localStorage.getItem('kokpeet_user_metrics');
    
    if (savedMetrics) {
      const parsedMetrics = JSON.parse(savedMetrics);
      
      // Si on a des paramètres pour ce compte spécifique, les utiliser
      if (parsedMetrics[accountId]) {
        return parsedMetrics[accountId];
      }
      // Sinon, utiliser les paramètres globaux s'ils existent
      if (parsedMetrics.global) {
        return parsedMetrics.global;
      }
      // Ou les paramètres de l'ancien format pour rétrocompatibilité
      if (Array.isArray(parsedMetrics)) {
        return parsedMetrics;
      }
    }
  } catch (e) {
    console.error('Erreur lors du chargement des métriques:', e);
  }
  return DEFAULT_METRICS;
};

// Récupère les clés des métriques sélectionnées pour un compte spécifique
export const getSelectedMetricKeys = (accountId = 'global') => {
  const metrics = getSelectedMetrics(accountId);
  return metrics.filter(metric => metric.selected).map(metric => metric.key);
};

// Récupère les noms de colonnes des métriques sélectionnées pour un compte spécifique
export const getSelectedMetricColumns = (accountId = 'global') => {
  const selectedKeys = getSelectedMetricKeys(accountId);
  return selectedKeys.map(key => METRIC_COLUMN_MAPPING[key]).filter(Boolean);
};

// Filtre les données d'un tableau pour ne garder que les colonnes des métriques sélectionnées
export const filterDataBySelectedMetrics = (data, additionalColumns = [], accountId = 'global') => {
  if (!Array.isArray(data) || data.length === 0) return data;
  
  const selectedColumns = getSelectedMetricColumns(accountId);
  const columnsToKeep = [...additionalColumns, ...selectedColumns];
  
  return data.map(row => {
    const filteredRow = {};
    // Garder toujours certaines colonnes essentielles
    ['nom', 'name', 'id', 'campaign_id', 'campaign_name', 'ad_group_name', 'keyword', 'search_term', 'device', 'date'].forEach(col => {
      if (row[col] !== undefined) {
        filteredRow[col] = row[col];
      }
    });
    
    // Ajouter les colonnes spécifiées
    columnsToKeep.forEach(col => {
      if (row[col] !== undefined) {
        filteredRow[col] = row[col];
      }
    });
    
    return filteredRow;
  });
};

// Définit le compte actuellement actif (pour compatibilité avec les anciens composants)
export const setCurrentActiveAccount = (accountId) => {
  currentActiveAccountId = accountId || 'global';
};

// Récupère le compte actuellement actif
export const getCurrentActiveAccount = () => currentActiveAccountId;

// Vérifie si une métrique est sélectionnée pour un compte spécifique
export const isMetricSelected = (metricKey, accountId) => {
  // Si aucun accountId n'est fourni, utiliser le compte actif
  const targetAccountId = accountId || currentActiveAccountId;
  
  
  const selectedKeys = getSelectedMetricKeys(targetAccountId);
  const result = selectedKeys.includes(metricKey);
  
  
  return result;
};

// Labels spécifiques par plateforme
export const GA4_METRIC_LABELS = {
  visites: 'Sessions',
  visiteurs: 'Utilisateurs',
  // Dernier recours, pour un écran qui ne connaît pas l'événement de conversion retenu : sans
  // parenthèse, faute de pouvoir la calculer ici. Les écrans qui disposent de la configuration
  // affichent « % Conversion (<événement>) » via `libelleTauxConversion`.
  taux_conv: '% Conversion',
  conv: 'Conversions',
  pages_vues: 'Pages vues',
  rebond: '% de Rebond',
  duree_moy: 'Durée moyenne',
  pages_visite: 'Pages/Session',
  transactions: 'Transactions',
  chiffre_affaires: 'Chiffre d\'affaires',
  taux_transaction: '% Conversion (transactions)',
  panier_moyen: 'Panier moyen'
};

/**
 * ⚠️ **Les trois tables disent la même chose avec les mêmes mots.**
 *
 * Elles divergeaient : « Sessions » chez GA4 contre « Visites » chez Piano, « % de Rebond »
 * contre « Rebond », « Pages/Session » contre « Pages/Visite ». Trois tableaux côte à côte
 * semblaient mesurer trois choses, alors qu'ils mesurent la même.
 *
 * GA4 fait référence, parce qu'il porte la liste la plus complète. Une métrique ajoutée à
 * `GA4_METRIC_LABELS` se répercute donc ici et dans `MATOMO_METRIC_LABELS`.
 */
export const PIANO_METRIC_LABELS = {
  visites: 'Sessions',
  visiteurs: 'Utilisateurs',
  taux_conv: '% Conversion',
  conv: 'Conversions',
  pages_vues: 'Pages vues',
  rebond: '% de Rebond',
  duree_moy: 'Durée moyenne',
  pages_visite: 'Pages/Session',
  transactions: 'Transactions',
  chiffre_affaires: 'Chiffre d\'affaires',
  taux_transaction: '% Conversion (transactions)',
  panier_moyen: 'Panier moyen',
  // Conservée pour les tableaux Canaux et Géographie, qui l'affichent encore.
  valeur_conv: 'Valeur Conv.'
};

/**
 * Les libellés Meta Ads, et la raison d'être de cette table.
 *
 * La colonne « Conv. » du dashboard Meta ne comptait **que les achats** — `purchase`, avec deux
 * replis. Mesuré sur le parc : la plupart des comptes ne servent aucun de ces types, si bien
 * qu'un compte qui convertit affichait **0** partout. Le même nom désignait donc deux choses
 * selon le compte : un nombre d'achats, ou rien.
 *
 * La métrique est scindée, comme dans le dashboard Google Analytics :
 *
 * | Clé | Ce qu'elle compte | Configurable |
 * |---|---|---|
 * | `transactions` | les achats, et rien d'autre | non |
 * | `conv` | les types d'actions retenus par l'utilisateur | oui |
 *
 * ⚠️ **`conv` et `taux_conv` ne se nomment pas ici.** Leur intitulé dépend de ce que
 * l'utilisateur a choisi de compter : il vient de `libelleConversions` et
 * `libelleTauxConversion`, auxquels les écrans passent la configuration du compte. Les valeurs
 * ci-dessous sont le dernier recours, pour un écran qui ne l'a pas.
 */
/**
 * Les libellés d'une boutique PrestaShop.
 *
 * ⚠️ Trois colonnes, et aucun vocabulaire d'audience : une boutique ne parle pas de
 * « transactions » mais de **commandes**, et son chiffre d'affaires n'est pas une « valeur de
 * conversion ». Sans cette table, le repli générique poserait les libellés d'un tableau
 * publicitaire au-dessus de chiffres de commerce.
 */
export const PRESTASHOP_METRIC_LABELS = {
  transactions: 'Commandes',
  quantite: 'Quantités',
  prix_moyen: 'Prix moyen',
  chiffre_affaires: "Chiffre d'affaires",
  panier_moyen: 'Panier moyen'
};

export const FBADS_METRIC_LABELS = {
  impressions: 'Impressions',
  couverture: 'Couverture',
  repetition: 'Répétition',
  ctr: 'CTR',
  clics: 'Clics',
  cpc: 'CPC',
  cout: 'Coût',
  taux_conv: '% Conversion',
  conv: 'Conversions',
  cpa: 'CPA',
  valeur_conv: 'Valeur Conv.',
  cos: 'COS',
  roas: 'ROAS',
  roi: 'ROI',
  panier_moyen: 'Panier moyen',
  transactions: 'Transactions',
  interactions: 'Interactions',
  cout_par_interaction: 'Coût/Interaction',
  reactions: 'Réactions',
  cout_par_reaction: 'Coût/Réaction',
  commentaires: 'Commentaires',
  enregistrements: 'Enregistrements',
  partages: 'Partages',
  jaime_facebook: "J'aime (Facebook)",
  videos_vues: 'Vidéos vues',
  taux_videos_vues: '% vidéos vues'
};

export const MATOMO_METRIC_LABELS = {
  visites: 'Sessions',
  // Le tableau Résultats ne la propose pas — l'API la refuse sur une période glissante —,
  // mais Canaux et Géographie l'affichent encore, jour par jour.
  visiteurs: 'Utilisateurs',
  taux_conv: '% Conversion',
  conv: 'Conversions',
  pages_vues: 'Pages vues',
  rebond: '% de Rebond',
  duree_moy: 'Durée moyenne',
  pages_visite: 'Pages/Session',
  transactions: 'Transactions',
  chiffre_affaires: 'Chiffre d\'affaires',
  taux_transaction: '% Conversion (transactions)',
  panier_moyen: 'Panier moyen',
  valeur_conv: 'Valeur Conv.',
  events: 'Événements',
  actions_par_event: 'Événements/session',
};

// Obtient le label d'une métrique (avec support par plateforme et labels personnalisés globaux)
/**
 * Le seul nom saisi par l'utilisateur, ou `null` s'il n'en a pas choisi.
 *
 * Extrait de `getMetricLabel`, qui enchaîne ensuite sur les tables par plateforme. Deux
 * métriques — `conv` et `taux_conv` — portent un nom qui dépend de l'événement de conversion
 * retenu, donc introuvable dans une table figée. Leurs écrans ont besoin de savoir si
 * l'utilisateur a tranché, pour ne le remplacer par le nom calculé que dans le cas contraire.
 */
export const getCustomMetricLabel = (metricKey, componentId = null, accountId = null) => {
  if (!accountId) return null;
  try {
    const saved = localStorage.getItem('kokpeet_global_labels');
    if (!saved) return null;
    const parsed = JSON.parse(saved);
    const platform = getPlatformFromComponentId(componentId);
    return parsed?.[accountId]?.[platform]?.[metricKey] || null;
  } catch (e) {
    return null; // Réglages illisibles : on retombe sur le nom par défaut
  }
};

export const getMetricLabel = (metricKey, componentId = null, accountId = null) => {
  // 1. D'abord vérifier s'il y a un label global personnalisé (par plateforme)
  const customLabel = getCustomMetricLabel(metricKey, componentId, accountId);
  if (customLabel) {
    return customLabel;
  }

  // 2. Labels spécifiques par plateforme
  if (componentId) {
    if (componentId.startsWith('ga4_')) {
      return GA4_METRIC_LABELS[metricKey] || metricKey;
    }
    if (componentId.startsWith('piano_')) {
      return PIANO_METRIC_LABELS[metricKey] || metricKey;
    }
    if (componentId.startsWith('matomo_')) {
      return MATOMO_METRIC_LABELS[metricKey] || metricKey;
    }
    if (componentId.startsWith('fbads_')) {
      return FBADS_METRIC_LABELS[metricKey] || metricKey;
    }
    /*
     * ⚠️ Une boutique ne parle pas de « transactions » mais de **commandes**, et son chiffre
     * d'affaires n'est pas une « valeur de conversion ». Le repli générique rendrait les
     * libellés d'un tableau publicitaire au-dessus de chiffres de commerce.
     */
    if (componentId.startsWith('prestashop_')) {
      return PRESTASHOP_METRIC_LABELS[metricKey] || metricKey;
    }
  }
  // 3. Fallback sur les labels par défaut
  const metric = DEFAULT_METRICS.find(m => m.key === metricKey);
  return metric ? metric.label : metricKey;
};

// Hook pour écouter les changements de métriques pour un compte spécifique
export const useSelectedMetrics = (accountId = 'global') => {
  const [selectedMetrics, setSelectedMetrics] = useState(() => getSelectedMetrics(accountId));
  
  useEffect(() => {
    const handleStorageChange = () => {
      setSelectedMetrics(getSelectedMetrics(accountId));
    };
    
    // Écouter les changements du localStorage
    window.addEventListener('storage', handleStorageChange);
    
    // Écouter les changements manuels (même onglet)
    window.addEventListener('metricsChanged', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('metricsChanged', handleStorageChange);
    };
  }, [accountId]);
  
  return selectedMetrics;
};

// Hook pour récupérer les métriques avec contexte de compte automatique
export const useAccountMetrics = (selectedAccount = null) => {
  const accountId = selectedAccount?.id || 'global';
  return useSelectedMetrics(accountId);
};

// Version avec compatibilité pour les isMetricSelected avec compte automatique
export const useAccountMetricsContext = (selectedAccount = null) => {
  const accountId = selectedAccount?.id || 'global';
  const selectedMetrics = useSelectedMetrics(accountId);
  
  // Retourner une fonction isMetricSelected avec le contexte du compte
  const isMetricSelectedForAccount = useCallback((metricKey) => {
    return isMetricSelected(metricKey, accountId);
  }, [accountId]);
  
  return {
    selectedMetrics,
    isMetricSelected: isMetricSelectedForAccount,
    accountId
  };
};

// Sauvegarde les métriques pour un compte spécifique
export const saveMetricsForAccount = (metrics, accountId = 'global') => {
  try {
    let allMetrics = {};
    
    // Charger les métriques existantes
    const existing = localStorage.getItem('kokpeet_user_metrics');
    if (existing) {
      const parsed = JSON.parse(existing);
      if (typeof parsed === 'object' && !Array.isArray(parsed)) {
        allMetrics = parsed;
      }
    }
    
    // Sauvegarder pour ce compte
    allMetrics[accountId] = metrics;
    
    localStorage.setItem('kokpeet_user_metrics', JSON.stringify(allMetrics));
    
    // Déclencher l'événement de changement
    window.dispatchEvent(new Event('metricsChanged'));
    
    return true;
  } catch (e) {
    console.error('Erreur lors de la sauvegarde des métriques:', e);
    return false;
  }
};

// Récupère tous les comptes ayant des paramètres de métriques
export const getAllAccountsWithMetrics = () => {
  try {
    const savedMetrics = localStorage.getItem('kokpeet_user_metrics');
    if (savedMetrics) {
      const parsedMetrics = JSON.parse(savedMetrics);
      if (typeof parsedMetrics === 'object' && !Array.isArray(parsedMetrics)) {
        return Object.keys(parsedMetrics);
      }
    }
  } catch (e) {
    console.error('Erreur lors du chargement des comptes avec métriques:', e);
  }
  return [];
};

// Composant wrapper pour masquer/afficher une colonne selon les préférences
export const MetricColumn = ({ metricKey, children, className = "", onClick, accountId = 'global', ...props }) => {
  const isSelected = isMetricSelected(metricKey, accountId);
  
  if (!isSelected) {
    return null;
  }
  
  return (
    <th className={className} onClick={onClick} {...props}>
      {children}
    </th>
  );
};

// Composant wrapper pour les cellules de données
export const MetricCell = ({ metricKey, children, className = "", accountId = 'global', ...props }) => {
  const isSelected = isMetricSelected(metricKey, accountId);
  
  if (!isSelected) {
    return null;
  }
  
  return (
    <td className={className} {...props}>
      {children}
    </td>
  );
};

/**
 * Cache des réglages utilisateur (seuils et activation des couleurs COS/CPA).
 *
 * getCosClassName et getCpaClassName sont appelées par cellule de tableau, et chacune
 * déclenchait deux localStorage.getItem suivis de deux JSON.parse — une pour le seuil,
 * une pour l'activation. Sur une page de dix lignes affichant COS et CPA, cela faisait
 * une quarantaine de lectures synchrones par rendu, multipliées par le nombre de modules.
 *
 * Vidé à chaque écriture et sur l'événement settingsChanged déjà émis par l'application,
 * ainsi que sur les modifications venues d'un autre onglet.
 */
const SETTINGS_STORAGE_KEY = 'kokpeet_user_settings';
let settingsCache = null;

export const invalidateSettingsCache = () => {
  settingsCache = null;
};

const readSettingsStore = () => {
  if (settingsCache) return settingsCache;
  try {
    const saved = localStorage.getItem(SETTINGS_STORAGE_KEY);
    settingsCache = saved ? JSON.parse(saved) : {};
  } catch (e) {
    console.error('Erreur lors du chargement des réglages utilisateur:', e);
    settingsCache = {};
  }
  return settingsCache;
};

if (typeof window !== 'undefined') {
  window.addEventListener('settingsChanged', invalidateSettingsCache);
  window.addEventListener('storage', (event) => {
    if (!event.key || event.key === SETTINGS_STORAGE_KEY) invalidateSettingsCache();
  });
}

// Récupère le seuil COS pour un compte spécifique
export const getCosThreshold = (accountId = 'global') => {
  try {
    const parsedSettings = readSettingsStore();

    if (parsedSettings) {
      
      // Si on a des paramètres pour ce compte spécifique, les utiliser
      if (parsedSettings[accountId] && parsedSettings[accountId].cosThreshold !== undefined) {
        return parsedSettings[accountId].cosThreshold;
      }
      // Sinon, utiliser les paramètres globaux s'ils existent
      if (parsedSettings.global && parsedSettings.global.cosThreshold !== undefined) {
        return parsedSettings.global.cosThreshold;
      }
    }
  } catch (e) {
    console.error('Erreur lors du chargement du seuil COS:', e);
  }
  return DEFAULT_COS_THRESHOLD;
};

// Sauvegarde le seuil COS pour un compte spécifique
export const saveCosThreshold = (threshold, accountId = 'global') => {
  try {
    let allSettings = {};
    
    // Charger les paramètres existants
    const existing = localStorage.getItem('kokpeet_user_settings');
    if (existing) {
      const parsed = JSON.parse(existing);
      if (typeof parsed === 'object' && !Array.isArray(parsed)) {
        allSettings = parsed;
      }
    }
    
    // Initialiser les paramètres du compte si ils n'existent pas
    if (!allSettings[accountId]) {
      allSettings[accountId] = {};
    }
    
    // Sauvegarder le seuil pour ce compte
    allSettings[accountId].cosThreshold = threshold;
    
    localStorage.setItem('kokpeet_user_settings', JSON.stringify(allSettings));
    invalidateSettingsCache();
    
    // Déclencher l'événement de changement
    window.dispatchEvent(new Event('settingsChanged'));
    
    return true;
  } catch (e) {
    console.error('Erreur lors de la sauvegarde du seuil COS:', e);
    return false;
  }
};

// Hook pour écouter les changements de seuil COS
export const useCosThreshold = (accountId = 'global') => {
  const [cosThreshold, setCosThreshold] = useState(() => getCosThreshold(accountId));
  
  useEffect(() => {
    const handleSettingsChange = () => {
      setCosThreshold(getCosThreshold(accountId));
    };
    
    // Écouter les changements du localStorage
    window.addEventListener('storage', handleSettingsChange);
    
    // Écouter les changements manuels (même onglet)
    window.addEventListener('settingsChanged', handleSettingsChange);
    
    return () => {
      window.removeEventListener('storage', handleSettingsChange);
      window.removeEventListener('settingsChanged', handleSettingsChange);
    };
  }, [accountId]);
  
  return cosThreshold;
};

/**
 * Panier moyen d'une ligne de régie : valeur de conversion divisée par nombre de conversions.
 *
 * Aucune API publicitaire ne rend cette métrique — elle se calcule, et c'est pourquoi elle
 * manquait à plusieurs tableaux alors qu'elle figurait dans leurs métriques disponibles : le
 * sélecteur la proposait, le template E-commerce la retenait, et aucune colonne ne l'accueillait.
 * Une métrique cochée sans colonne ne laisse aucune trace à l'écran, si bien que c'est le
 * template qui semblait ne pas s'appliquer.
 *
 * Une seule définition pour tous les tableaux de régie : trois recopies, c'était trois occasions
 * de diverger sur le traitement du dénominateur nul.
 *
 * ⚠️ Ne convient pas aux outils de mesure (GA4, Piano, Matomo), dont les lignes portent
 * `transactions` et `totalRevenue` — voir `panierMoyenDe` dans `WeekdaysTable`, le seul tableau
 * à servir les deux familles.
 */
export const panierMoyenPublicitaire = (ligne) => {
  const conversions = ligne?.conversions || 0;
  return conversions > 0 ? (ligne.conversionsValue || 0) / conversions : 0;
};

// Fonction utilitaire pour déterminer la classe CSS du COS basée sur le seuil
export const getCosClassName = (cosValue, accountId = 'global', componentId = null) => {
  // Si componentId fourni, vérifier d'abord le nouveau système d'objectifs
  if (componentId) {
    const objective = getMetricObjective('cos', componentId, accountId);
    if (objective.enabled && objective.value !== null && objective.value !== undefined) {
      const numericCos = parseFloat(cosValue) || 0;
      const targetValue = parseFloat(objective.value) || 0;
      if (numericCos === 0) return 'negative';
      // COS: lower is better
      return numericCos <= targetValue ? 'positive' : 'negative';
    }
  }

  // Fallback: ancien système
  if (!getCosColorEnabled(accountId)) {
    return ''; // Pas de classe de couleur si désactivé
  }

  const threshold = getCosThreshold(accountId);
  const numericCos = parseFloat(cosValue) || 0;

  if (numericCos === 0) return 'negative';
  return numericCos <= threshold ? 'positive' : 'negative';
};

// Fonction utilitaire pour calculer le COS et retourner la classe CSS
export const calculateCosClassName = (cost, conversionsValue, accountId = 'global', componentId = null) => {
  const numericCost = parseFloat(cost) || 0;
  const numericConversionsValue = parseFloat(conversionsValue) || 0;

  // Si pas de valeur, pas de couleur (sauf si un objectif est explicitement défini)
  if (numericConversionsValue === 0 || numericCost === 0) {
    // Vérifier si un objectif est défini avant d'appliquer une couleur
    if (componentId) {
      const objective = getMetricObjective('cos', componentId, accountId);
      if (objective.enabled && objective.value !== null && objective.value !== undefined) {
        return 'negative';
      }
    }
    // Fallback ancien système
    if (getCosColorEnabled(accountId)) {
      return 'negative';
    }
    return '';
  }

  const cos = (numericCost / numericConversionsValue) * 100;
  return getCosClassName(cos, accountId, componentId);
};

// ===== FONCTIONS CPA =====

// Récupère le seuil CPA pour un compte spécifique
export const getCpaThreshold = (accountId = 'global') => {
  try {
    const parsedSettings = readSettingsStore();

    if (parsedSettings) {
      
      // Si on a des paramètres pour ce compte spécifique, les utiliser
      if (parsedSettings[accountId] && parsedSettings[accountId].cpaThreshold !== undefined) {
        return parsedSettings[accountId].cpaThreshold;
      }
      // Sinon, utiliser les paramètres globaux s'ils existent
      if (parsedSettings.global && parsedSettings.global.cpaThreshold !== undefined) {
        return parsedSettings.global.cpaThreshold;
      }
    }
  } catch (e) {
    console.error('Erreur lors du chargement du seuil CPA:', e);
  }
  return DEFAULT_CPA_THRESHOLD;
};

// Sauvegarde le seuil CPA pour un compte spécifique
export const saveCpaThreshold = (threshold, accountId = 'global') => {
  try {
    let allSettings = {};
    
    // Charger les paramètres existants
    const existing = localStorage.getItem('kokpeet_user_settings');
    if (existing) {
      const parsed = JSON.parse(existing);
      if (typeof parsed === 'object' && !Array.isArray(parsed)) {
        allSettings = parsed;
      }
    }
    
    // Initialiser les paramètres du compte si ils n'existent pas
    if (!allSettings[accountId]) {
      allSettings[accountId] = {};
    }
    
    // Sauvegarder le seuil pour ce compte
    allSettings[accountId].cpaThreshold = threshold;
    
    localStorage.setItem('kokpeet_user_settings', JSON.stringify(allSettings));
    invalidateSettingsCache();
    
    // Déclencher l'événement de changement
    window.dispatchEvent(new Event('settingsChanged'));
    
    return true;
  } catch (e) {
    console.error('Erreur lors de la sauvegarde du seuil CPA:', e);
    return false;
  }
};

// Hook pour écouter les changements de seuil CPA
export const useCpaThreshold = (accountId = 'global') => {
  const [cpaThreshold, setCpaThreshold] = useState(() => getCpaThreshold(accountId));
  
  useEffect(() => {
    const handleSettingsChange = () => {
      setCpaThreshold(getCpaThreshold(accountId));
    };
    
    // Écouter les changements du localStorage
    window.addEventListener('storage', handleSettingsChange);
    
    // Écouter les changements manuels (même onglet)
    window.addEventListener('settingsChanged', handleSettingsChange);
    
    return () => {
      window.removeEventListener('storage', handleSettingsChange);
      window.removeEventListener('settingsChanged', handleSettingsChange);
    };
  }, [accountId]);
  
  return cpaThreshold;
};

// Fonction utilitaire pour déterminer la classe CSS du CPA basée sur le seuil
export const getCpaClassName = (cpaValue, accountId = 'global', componentId = null) => {
  // Si componentId fourni, vérifier d'abord le nouveau système d'objectifs
  if (componentId) {
    const objective = getMetricObjective('cpa', componentId, accountId);
    if (objective.enabled && objective.value !== null && objective.value !== undefined) {
      const numericCpa = parseFloat(cpaValue) || 0;
      const targetValue = parseFloat(objective.value) || 0;
      if (numericCpa === 0) return 'negative';
      // CPA: lower is better
      return numericCpa <= targetValue ? 'positive' : 'negative';
    }
  }

  // Fallback: ancien système
  const colorEnabled = getCpaColorEnabled(accountId);
  if (!colorEnabled) {
    return ''; // Pas de classe de couleur si désactivé
  }

  const threshold = getCpaThreshold(accountId);
  const numericCpa = parseFloat(cpaValue) || 0;

  if (numericCpa === 0) return 'negative';
  return numericCpa <= threshold ? 'positive' : 'negative';
};

// Fonction utilitaire pour calculer le CPA et retourner la classe CSS
export const calculateCpaClassName = (cost, conversions, accountId = 'global', componentId = null) => {
  const numericCost = parseFloat(cost) || 0;
  const numericConversions = parseFloat(conversions) || 0;

  if (numericConversions === 0 || numericCost === 0) return 'negative';

  const cpa = numericCost / numericConversions;
  return getCpaClassName(cpa, accountId, componentId);
};

// ===== FONCTIONS D'ACTIVATION/DÉSACTIVATION DES COULEURS =====

// Récupère l'état d'activation des couleurs COS pour un compte spécifique
export const getCosColorEnabled = (accountId = 'global') => {
  try {
    const parsedSettings = readSettingsStore();

    if (parsedSettings) {
      
      // Si on a des paramètres pour ce compte spécifique, les utiliser
      if (parsedSettings[accountId] && parsedSettings[accountId].cosColorEnabled !== undefined) {
        return parsedSettings[accountId].cosColorEnabled;
      }
      // Sinon, utiliser les paramètres globaux s'ils existent
      else if (accountId !== 'global' && parsedSettings['global'] && parsedSettings['global'].cosColorEnabled !== undefined) {
        return parsedSettings['global'].cosColorEnabled;
      }
    }
    
    // Par défaut, les couleurs sont désactivées (opt-in)
    return false;
  } catch (e) {
    console.error('Erreur lors de la récupération de l\'état des couleurs COS:', e);
    return false;
  }
};

// Sauvegarde l'état d'activation des couleurs COS pour un compte spécifique
export const setCosColorEnabled = (enabled, accountId = 'global') => {
  try {
    let allSettings = {};
    const existing = localStorage.getItem('kokpeet_user_settings');
    
    if (existing) {
      const parsed = JSON.parse(existing);
      if (typeof parsed === 'object' && !Array.isArray(parsed)) {
        allSettings = parsed;
      }
    }
    
    // Initialiser les paramètres du compte si ils n'existent pas
    if (!allSettings[accountId]) {
      allSettings[accountId] = {};
    }
    
    // Sauvegarder l'état pour ce compte
    allSettings[accountId].cosColorEnabled = enabled;
    
    localStorage.setItem('kokpeet_user_settings', JSON.stringify(allSettings));
    invalidateSettingsCache();
    
    // Déclencher l'événement de changement
    window.dispatchEvent(new Event('settingsChanged'));
    
    console.log(`💾 [COS-COLOR] État couleurs COS sauvegardé pour ${accountId}: ${enabled}`);
    return true;
  } catch (e) {
    console.error('Erreur lors de la sauvegarde de l\'état des couleurs COS:', e);
    return false;
  }
};

// Récupère l'état d'activation des couleurs CPA pour un compte spécifique
export const getCpaColorEnabled = (accountId = 'global') => {
  try {
    const parsedSettings = readSettingsStore();

    if (parsedSettings) {
      
      // Si on a des paramètres pour ce compte spécifique, les utiliser
      if (parsedSettings[accountId] && parsedSettings[accountId].cpaColorEnabled !== undefined) {
        return parsedSettings[accountId].cpaColorEnabled;
      }
      // Sinon, utiliser les paramètres globaux s'ils existent
      else if (accountId !== 'global' && parsedSettings['global'] && parsedSettings['global'].cpaColorEnabled !== undefined) {
        return parsedSettings['global'].cpaColorEnabled;
      }
    }
    
    // Par défaut, les couleurs sont désactivées (opt-in)
    return false;
  } catch (e) {
    console.error('Erreur lors de la récupération de l\'état des couleurs CPA:', e);
    return false;
  }
};

// Sauvegarde l'état d'activation des couleurs CPA pour un compte spécifique
export const setCpaColorEnabled = (enabled, accountId = 'global') => {
  try {
    let allSettings = {};
    const existing = localStorage.getItem('kokpeet_user_settings');
    
    if (existing) {
      const parsed = JSON.parse(existing);
      if (typeof parsed === 'object' && !Array.isArray(parsed)) {
        allSettings = parsed;
      }
    }
    
    // Initialiser les paramètres du compte si ils n'existent pas
    if (!allSettings[accountId]) {
      allSettings[accountId] = {};
    }
    
    // Sauvegarder l'état pour ce compte
    allSettings[accountId].cpaColorEnabled = enabled;
    
    localStorage.setItem('kokpeet_user_settings', JSON.stringify(allSettings));
    invalidateSettingsCache();
    
    // Déclencher l'événement de changement
    window.dispatchEvent(new Event('settingsChanged'));
    
    console.log(`💾 [CPA-COLOR] État couleurs CPA sauvegardé pour ${accountId}: ${enabled}`);
    return true;
  } catch (e) {
    console.error('Erreur lors de la sauvegarde de l\'état des couleurs CPA:', e);
    return false;
  }
};

// ===== OBJECTIFS PAR MÉTRIQUE =====

/**
 * Configuration des métriques ratios qui peuvent avoir un objectif.
 * Les métriques de valeur absolue (sessions, conversions, etc.) ne peuvent pas avoir d'objectif
 * car elles dépendent d'une période de temps.
 *
 * lowerIsBetter: true = vert si valeur ≤ objectif (ex: COS, CPA, rebond)
 * lowerIsBetter: false = vert si valeur ≥ objectif (ex: taux conv, ROAS, CTR)
 */
export const RATIO_METRICS = {
  ctr: { unit: '%', lowerIsBetter: false, label: 'CTR' },
  cpc: { unit: '€', lowerIsBetter: true, label: 'CPC' },
  taux_conv: { unit: '%', lowerIsBetter: false, label: '% Conversion' },
  // `taux_transaction` manquait à cette table : `isMetricRatio` le déclarait donc « non-ratio »,
  // et sa carte restait seule sans formule au milieu des autres ratios. Elle en est pourtant un.
  taux_transaction: { unit: '%', lowerIsBetter: false, label: '% Conversion (transactions)' },
  cpa: { unit: '€', lowerIsBetter: true, label: 'CPA' },
  cos: { unit: '%', lowerIsBetter: true, label: 'COS' },
  roas: { unit: '%', lowerIsBetter: false, label: 'ROAS' },
  roi: { unit: 'x', lowerIsBetter: false, label: 'ROI' },
  panier_moyen: { unit: '€', lowerIsBetter: false, label: 'Panier moyen' },
  rebond: { unit: '%', lowerIsBetter: true, label: '% Rebond' },
  pages_visite: { unit: '', lowerIsBetter: false, label: 'Pages/visite' },
  duree_moy: { unit: 's', lowerIsBetter: false, label: 'Durée moy.' },
  cout_par_interaction: { unit: '€', lowerIsBetter: true, label: 'Coût/interaction' },
  cout_par_reaction: { unit: '€', lowerIsBetter: true, label: 'Coût/réaction' },
  cpm: { unit: '€', lowerIsBetter: true, label: 'CPM' },
  cpv: { unit: '€', lowerIsBetter: true, label: 'CPV' },
  vtr: { unit: '%', lowerIsBetter: false, label: 'VTR' },
  taux_engagement: { unit: '%', lowerIsBetter: false, label: 'Taux engagement' },
  taux_videos_vues: { unit: '%', lowerIsBetter: false, label: '% vidéos vues' }
};

/**
 * Vérifie si une métrique est un ratio (peut avoir un objectif)
 */
/**
 * Le nom de l'événement compté comme conversion, tel qu'on l'affiche.
 *
 * `conversionEvents` porte le nom technique choisi sous la métrique Conversions
 * (`add_to_cart`). La valeur `transactions` y est la **sentinelle** du comportement standard :
 * le serveur bascule alors sur la métrique GA4 `conversions`, c'est-à-dire les événements
 * clés — et non sur le nombre d'achats. Écrire « transactions » rendrait cette métrique
 * indistinguable de `taux_transaction`, qui compte, elle, de vrais achats.
 *
 * La table des libellés français vit côté serveur, et la contrainte de portée de Create React
 * App interdit de l'importer : les écrans qui n'ont que le nom technique l'affichent tel quel,
 * ce qui reste plus informatif qu'un intitulé ne disant pas ce qu'il compte. La modale de
 * configuration, elle, dispose des libellés et affiche « Ajout au panier ».
 */
export const libelleEvenementConversion = (conversionConfig, plateforme = null) => {
  const evenements = conversionConfig?.conversionEvents;
  if (!evenements || evenements.length === 0 || evenements[0] === 'transactions') {
    /*
     * La sentinelle ne désigne pas la même mesure partout, et le mot doit suivre.
     *
     * Chez Meta elle vaut « les achats » : écrire « événements clés » y renverrait à une
     * notion de Google Analytics que Meta ne connaît pas, et ferait chercher un réglage
     * inexistant. Le même soin que `optionParDefaut` dans la modale de configuration.
     */
    return plateforme === 'fbads' ? 'achats' : 'événements clés';
  }
  /*
   * Le libellé français quand le serveur l'a joint.
   *
   * La table des noms (« Achat » pour `purchase`) vit côté serveur et la contrainte de portée
   * de Create React App interdit de l'importer : le serveur la fait donc descendre dans
   * `libelles`, aux côtés de la configuration. Sans elle, l'écran affiche le nom technique —
   * exact, mais c'est de la plomberie exposée à l'utilisateur.
   *
   * ⚠️ Deux conversions retenues ou plus : on annonce leur nombre plutôt que de les énumérer.
   * Trois noms dans l'intitulé d'une colonne la rendraient trois fois plus large que son
   * contenu, et c'est la fenêtre de configuration qui porte le détail.
   */
  /*
   * ⚠️ **Une seule source : son nom. Deux ou plus : leur nombre.**
   *
   * Un en-tête de colonne a la largeur d'un nombre, pas d'une énumération. Deux conversions
   * personnalisées nommées donnaient
   * « Conversions (offsite_conversion.custom.1418277969749982 + offsite_conversion.custom.
   * 2092268048077021) » : sept lignes de titre au-dessus d'une colonne de deux chiffres.
   *
   * Le détail de ce qui est compté se lit dans la fenêtre de configuration, qui a la place de
   * l'afficher ; l'en-tête, lui, dit seulement combien de sources alimentent la mesure.
   */
  if (evenements.length >= 2) return `${evenements.length} sources`;
  return conversionConfig?.libelles?.[evenements[0]] || evenements[0];
};

/** Le nom complet de la métrique de taux, qui dit enfin ce qu'elle compte. */
/**
 * Ce que compte la conversion, en un mot : le nom saisi à la main s'il existe, sinon
 * l'événement. C'est la parenthèse commune aux deux métriques — sans quoi `Conversions`
 * afficherait « Paniers » pendant que le taux d'à côté afficherait « add_to_cart ».
 */
export const nomDeLaConversion = (conversionConfig, conversionLabel = null, plateforme = null) => {
  const nomChoisi = (conversionLabel ?? conversionConfig?.conversionLabel ?? '').trim();
  if (nomChoisi && nomChoisi !== 'Conversions') return nomChoisi;
  return libelleEvenementConversion(conversionConfig, plateforme);
};

/**
 * Un graphe de répartition montre-t-il les transactions plutôt que les conversions ?
 *
 * Un camembert ou un histogramme ne porte **qu'une** mesure : il faut choisir. La règle est
 * celle du dashboard Google Analytics — les transactions l'emportent seulement si elles sont
 * cochées *et* que les conversions ne le sont pas. Autrement dit on ne retire jamais à
 * l'utilisateur la mesure qu'il a explicitement demandée : cocher Transactions **en plus** de
 * Conversions ne change pas le graphe, cocher Transactions **à la place** le change.
 *
 * La règle vit ici et non dans chaque graphe : elle était déjà écrite deux fois côté GA4, et
 * quatre écrans Meta la réclamaient. Deux copies, c'est deux occasions de diverger.
 *
 * @param {(cle: string) => boolean} estActive - le prédicat d'activité du composant
 */
export const grapheSurTransactions = (estActive) =>
  Boolean(estActive && estActive('transactions') && !estActive('conv'));

export const libelleTauxConversion = (conversionConfig, conversionLabel = null, plateforme = null) =>
  `% Conversion (${nomDeLaConversion(conversionConfig, conversionLabel, plateforme)})`;

/**
 * Les sources que compte la métrique Conversions, nommées.
 *
 * `conversionEvents` porte les noms techniques ; `libelles`, joint par le serveur, porte leur
 * traduction — y compris celle des conversions personnalisées, dont le nom ne vit que chez Meta.
 * On retombe sur le nom technique plutôt que de masquer une source faute de traduction : exact
 * et illisible vaut mieux qu'une liste incomplète.
 *
 * Tableau vide quand rien n'est configuré, la sentinelle `transactions` valant « le défaut ».
 */
export const sourcesDeConversion = (conversionConfig) => {
  const evenements = conversionConfig?.conversionEvents;
  if (!evenements || evenements.length === 0 || evenements[0] === 'transactions') return [];
  return evenements.map(type => conversionConfig?.libelles?.[type] || type);
};

/**
 * Ce que l'en-tête des colonnes Conversions et Taux de conversion dit au survol.
 *
 * L'en-tête annonce « 2 sources » dès qu'il y en a plusieurs — trois noms y tiendraient une
 * largeur que la colonne n'a pas. Le détail devait donc se lire quelque part sans ouvrir la
 * fenêtre de configuration : c'est ici.
 *
 * Rendu dès qu'il y a une source, même unique, où il répète l'en-tête : le survol doit
 * répondre à la même question partout.
 *
 * ⚠️ **Rien quand aucune source n'est configurée**, et surtout pas « les achats par défaut ».
 * `CampaignsTable` sert toutes les régies, et un compte Meta sans configuration y est
 * indiscernable d'un compte Google Ads : les deux arrivent avec `conversionConfig` à `null`.
 * Une phrase sur le comportement Meta s'afficherait donc sur des colonnes Google. L'absence
 * d'infobulle dit exactement la vérité — aucune source choisie —, et l'en-tête annonce déjà
 * « Conversions (achats) » de son côté.
 */
export const infobulleConversions = (conversionConfig) => {
  const sources = sourcesDeConversion(conversionConfig);
  if (sources.length === 0) return null;
  if (sources.length === 1) return `Source comptée : ${sources[0]}`;
  return `${sources.length} sources comptées :\n• ${sources.join('\n• ')}`;
};

/**
 * L'infobulle d'un en-tête de colonne — vide sauf sur Conversions et Taux de conversion.
 *
 * Neuf tableaux portent ces deux colonnes, sous trois formes de JSX différentes. La condition
 * vit donc ici : recopiée neuf fois, elle aurait dérivé au premier tableau ajouté, et une
 * colonne sans infobulle se lirait comme une colonne sans configuration.
 *
 * Rend `undefined` et non `null` : React n'écrit alors aucun attribut `title`, là où `null`
 * en poserait un vide et ferait apparaître une infobulle blanche au survol.
 */
export const infobulleEnteteConversion = (metricKey, conversionConfig) => {
  if (metricKey !== 'conv' && metricKey !== 'taux_conv') return undefined;
  return infobulleConversions(conversionConfig) || undefined;
};

/**
 * Le nom de la métrique Conversions, qui dit quel événement elle compte.
 *
 * `conversionLabel` est le nom saisi à la main dans la modale : il l'emporte toujours, et
 * `'Conversions'` y est la valeur de repli, pas un choix — c'est le seul cas où l'on complète.
 * Les sous-graphes reçoivent ce nom en propriété, parfois issu de leur propre jeu de données,
 * d'où le second paramètre.
 */
export const libelleConversions = (conversionConfig, conversionLabel = null, plateforme = null) => {
  const nomChoisi = (conversionLabel ?? conversionConfig?.conversionLabel ?? '').trim();
  if (nomChoisi && nomChoisi !== 'Conversions') return nomChoisi;
  return `Conversions (${libelleEvenementConversion(conversionConfig, plateforme)})`;
};

export const isMetricRatio = (metricKey) => {
  return RATIO_METRICS.hasOwnProperty(metricKey);
};

/**
 * Récupère la configuration d'un ratio (unit, lowerIsBetter, label)
 */
export const getRatioMetricConfig = (metricKey) => {
  return RATIO_METRICS[metricKey] || null;
};

/**
 * Cache du fichier d'objectifs.
 *
 * getMetricObjective est appelé par cellule de tableau, pour chacune des dix métriques de
 * type ratio. Sans cache, chaque cellule déclenchait un localStorage.getItem suivi d'un
 * JSON.parse de l'intégralité du fichier pour n'en extraire qu'une valeur — soit plusieurs
 * centaines de lectures synchrones, donc bloquantes, à chaque rendu du dashboard.
 *
 * Le cache est vidé à chaque écriture (les trois emplacements appellent
 * invalidateObjectivesCache) et sur l'événement métier déjà émis par l'application, ainsi
 * que sur les modifications venues d'un autre onglet.
 */
const OBJECTIVES_STORAGE_KEY = 'kokpeet_metric_objectives';
let objectivesCache = null;

export const invalidateObjectivesCache = () => {
  objectivesCache = null;
};

const readObjectivesStore = () => {
  if (objectivesCache) return objectivesCache;
  try {
    const saved = localStorage.getItem(OBJECTIVES_STORAGE_KEY);
    objectivesCache = saved ? JSON.parse(saved) : {};
  } catch (e) {
    console.error('Erreur chargement objectifs métriques:', e);
    objectivesCache = {};
  }
  return objectivesCache;
};

if (typeof window !== 'undefined') {
  window.addEventListener('metricObjectivesChanged', invalidateObjectivesCache);
  window.addEventListener('storage', (event) => {
    if (!event.key || event.key === OBJECTIVES_STORAGE_KEY) invalidateObjectivesCache();
  });
}

/**
 * Récupère l'objectif d'une métrique (par plateforme)
 * @returns {{ value: number|null, enabled: boolean }}
 */
export const getMetricObjective = (metricKey, componentId = null, accountId = 'global') => {
  const platform = getPlatformFromComponentId(componentId);
  const objective = readObjectivesStore()?.[accountId]?.[platform]?.[metricKey];
  return objective || { value: null, enabled: false };
};

/**
 * Récupère tous les objectifs pour un compte et une plateforme
 * @returns {{ [metricKey]: { value: number|null, enabled: boolean } }}
 */
export const getAllMetricObjectives = (componentId = null, accountId = 'global') => {
  const platform = getPlatformFromComponentId(componentId);
  return readObjectivesStore()?.[accountId]?.[platform] || {};
};

/**
 * Sauvegarde l'objectif d'une métrique (par plateforme)
 */
export const saveMetricObjective = (metricKey, componentId = null, accountId = 'global', value, enabled) => {
  const platform = getPlatformFromComponentId(componentId);
  try {
    let all = {};
    const existing = localStorage.getItem('kokpeet_metric_objectives');
    if (existing) {
      all = JSON.parse(existing);
    }

    if (!all[accountId]) all[accountId] = {};
    if (!all[accountId][platform]) all[accountId][platform] = {};

    all[accountId][platform][metricKey] = { value, enabled };

    localStorage.setItem('kokpeet_metric_objectives', JSON.stringify(all));
    invalidateObjectivesCache();
    window.dispatchEvent(new Event('metricObjectivesChanged'));

    // Sauvegarder aussi vers l'API en arrière-plan
    saveToApi(null, null, all);

    console.log(`💾 [METRIC-OBJECTIVE] Sauvegardé ${metricKey} pour ${platform}/${accountId}: ${value} (${enabled ? 'activé' : 'désactivé'})`);
    return true;
  } catch (e) {
    console.error('Erreur sauvegarde objectif métrique:', e);
    return false;
  }
};

/**
 * Sauvegarde tous les objectifs d'un coup (par plateforme)
 * @param objectives {{ [metricKey]: { value: number|null, enabled: boolean } }}
 */
export const saveAllMetricObjectives = (componentId = null, accountId = 'global', objectives, applyToAll = false) => {
  const platform = getPlatformFromComponentId(componentId);
  try {
    let all = {};
    const existing = localStorage.getItem('kokpeet_metric_objectives');
    if (existing) {
      all = JSON.parse(existing);
    }

    if (!all[accountId]) all[accountId] = {};
    if (!all[accountId][platform]) all[accountId][platform] = {};

    // Fusionner avec les objectifs existants pour cette plateforme
    Object.entries(objectives).forEach(([metricKey, objective]) => {
      all[accountId][platform][metricKey] = objective;
    });

    console.log(`💾 [METRIC-OBJECTIVES] Sauvegardé ${Object.keys(objectives).length} objectifs pour ${platform}/${accountId}`);

    localStorage.setItem('kokpeet_metric_objectives', JSON.stringify(all));
    invalidateObjectivesCache();
    window.dispatchEvent(new Event('metricObjectivesChanged'));

    // Sauvegarder aussi vers l'API en arrière-plan
    saveToApi(null, null, all);

    return true;
  } catch (e) {
    console.error('Erreur sauvegarde objectifs métriques:', e);
    return false;
  }
};

/**
 * Retourne la classe CSS ('positive', 'negative', ou '') basée sur l'objectif
 * @param metricKey - Clé de la métrique
 * @param value - Valeur actuelle de la métrique
 * @param componentId - ID du composant
 * @param accountId - ID du compte
 * @returns 'positive' | 'negative' | ''
 */
export const getMetricObjectiveClassName = (metricKey, value, componentId, accountId = 'global') => {
  // Vérifier si c'est une métrique ratio
  const ratioConfig = RATIO_METRICS[metricKey];
  if (!ratioConfig) return '';

  // Récupérer l'objectif
  const objective = getMetricObjective(metricKey, componentId, accountId);
  if (!objective.enabled || objective.value === null || objective.value === undefined) {
    return '';
  }

  const numericValue = parseFloat(value) || 0;
  const targetValue = parseFloat(objective.value) || 0;

  // Appliquer la logique selon lowerIsBetter
  if (ratioConfig.lowerIsBetter) {
    // Vert si valeur ≤ objectif (ex: COS, CPA)
    return numericValue <= targetValue ? 'positive' : 'negative';
  } else {
    // Vert si valeur ≥ objectif (ex: taux conv, ROAS)
    return numericValue >= targetValue ? 'positive' : 'negative';
  }
};

/**
 * Hook pour écouter les changements d'objectifs de métriques
 */
export const useMetricObjectives = (componentId, accountId = 'global') => {
  const [objectives, setObjectives] = useState(() => getAllMetricObjectives(componentId, accountId));

  useEffect(() => {
    const handleChange = () => {
      setObjectives(getAllMetricObjectives(componentId, accountId));
    };
    window.addEventListener('storage', handleChange);
    window.addEventListener('metricObjectivesChanged', handleChange);
    return () => {
      window.removeEventListener('storage', handleChange);
      window.removeEventListener('metricObjectivesChanged', handleChange);
    };
  }, [componentId, accountId]);

  return objectives;
};

// ===== LABELS PERSONNALISÉS PAR PLATEFORME =====

/**
 * Détermine la plateforme à partir d'un componentId
 */
export const getPlatformFromComponentId = (componentId) => {
  if (!componentId) return 'gads';
  if (componentId.startsWith('fbads_')) return 'fbads';
  if (componentId.startsWith('msads_')) return 'msads';
  if (componentId.startsWith('ga4_')) return 'ga4';
  if (componentId.startsWith('piano_')) return 'piano';
  if (componentId.startsWith('matomo_')) return 'matomo';
  if (componentId.startsWith('chatgpt_')) return 'chatgpt';
  if (componentId.startsWith('awin_')) return 'awin';
  return 'gads'; // Par défaut Google Ads
};

/**
 * Récupère un label personnalisé pour une métrique (par plateforme)
 * @returns {string|null}
 */
export const getGlobalLabel = (metricKey, accountId = 'global', platform = 'gads') => {
  try {
    const saved = localStorage.getItem('kokpeet_global_labels');
    if (saved) {
      const parsed = JSON.parse(saved);
      return parsed?.[accountId]?.[platform]?.[metricKey] || null;
    }
  } catch (e) {
    console.error('Erreur chargement label global:', e);
  }
  return null;
};

/**
 * Récupère tous les labels personnalisés pour un compte et une plateforme
 * @returns {{ [metricKey]: string }}
 */
export const getAllGlobalLabels = (accountId = 'global', platform = 'gads') => {
  try {
    const saved = localStorage.getItem('kokpeet_global_labels');
    if (saved) {
      const parsed = JSON.parse(saved);
      return parsed?.[accountId]?.[platform] || {};
    }
  } catch (e) {
    console.error('Erreur chargement labels globaux:', e);
  }
  return {};
};

/**
 * Sauvegarde un label personnalisé (par plateforme)
 */
export const saveGlobalLabel = (metricKey, label, accountId = 'global', platform = 'gads') => {
  try {
    let all = {};
    const existing = localStorage.getItem('kokpeet_global_labels');
    if (existing) {
      all = JSON.parse(existing);
    }

    if (!all[accountId]) all[accountId] = {};
    if (!all[accountId][platform]) all[accountId][platform] = {};

    if (label && label.trim()) {
      all[accountId][platform][metricKey] = label.trim();
    } else {
      delete all[accountId][platform][metricKey];
    }

    localStorage.setItem('kokpeet_global_labels', JSON.stringify(all));
    window.dispatchEvent(new Event('globalLabelsChanged'));

    // Sauvegarder aussi vers l'API en arrière-plan
    saveToApi(null, all, null);

    console.log(`💾 [GLOBAL-LABEL] Sauvegardé ${metricKey} pour ${platform}/${accountId}: "${label}"`);
    return true;
  } catch (e) {
    console.error('Erreur sauvegarde label global:', e);
    return false;
  }
};

/**
 * Sauvegarde tous les labels personnalisés d'un coup (par plateforme)
 * @param labels {{ [metricKey]: string }}
 */
export const saveAllGlobalLabels = (labels, accountId = 'global', platform = 'gads') => {
  try {
    let all = {};
    const existing = localStorage.getItem('kokpeet_global_labels');
    if (existing) {
      all = JSON.parse(existing);
    }

    if (!all[accountId]) all[accountId] = {};
    if (!all[accountId][platform]) all[accountId][platform] = {};

    // Fusionner avec les labels existants
    Object.entries(labels).forEach(([metricKey, label]) => {
      if (label && label.trim()) {
        all[accountId][platform][metricKey] = label.trim();
      } else {
        delete all[accountId][platform][metricKey];
      }
    });

    localStorage.setItem('kokpeet_global_labels', JSON.stringify(all));
    window.dispatchEvent(new Event('globalLabelsChanged'));

    // Sauvegarder aussi vers l'API en arrière-plan
    saveToApi(null, all, null);

    console.log(`💾 [GLOBAL-LABELS] Sauvegardé ${Object.keys(labels).length} labels pour ${platform}/${accountId}`);
    return true;
  } catch (e) {
    console.error('Erreur sauvegarde labels globaux:', e);
    return false;
  }
};

/**
 * Hook pour écouter les changements de labels (par plateforme)
 */
export const useGlobalLabels = (accountId = 'global', platform = 'gads') => {
  const [labels, setLabels] = useState(() => getAllGlobalLabels(accountId, platform));

  useEffect(() => {
    const handleChange = () => {
      setLabels(getAllGlobalLabels(accountId, platform));
    };
    window.addEventListener('storage', handleChange);
    window.addEventListener('globalLabelsChanged', handleChange);
    return () => {
      window.removeEventListener('storage', handleChange);
      window.removeEventListener('globalLabelsChanged', handleChange);
    };
  }, [accountId, platform]);

  return labels;
};

/**
 * Récupère le label d'une métrique en tenant compte des labels personnalisés
 * Priorité: label personnalisé (plateforme) > label par défaut
 */
export const getMetricLabelWithCustom = (metricKey, accountId = 'global', componentId = null) => {
  const platform = getPlatformFromComponentId(componentId);

  // 1. Vérifier s'il y a un label personnalisé pour cette plateforme
  const customLabel = getGlobalLabel(metricKey, accountId, platform);
  if (customLabel) {
    return customLabel;
  }

  // 2. Sinon utiliser le label par défaut via getMetricLabel
  return getMetricLabel(metricKey, componentId);
};

// ===== CONFIGURATION DES MÉTRIQUES PAR COMPOSANT =====

// Limites de métriques par composant
// Seuls les composants Top & Flop sont limités à 5-7 métriques, les autres à 12
export const COMPONENT_METRICS_LIMITS = {
  // Google Ads tables
  results: 12,
  // Dashboard comparatif : mêmes métriques et même plafond que Résultats — c'est le même
  // périmètre de données, lu période contre période au lieu de jour par jour.
  comparatif: 12,
  networks: 12,
  devices: 12,
  weekdays: 12,
  geography: 12,
  campaign_types: 12,
  campaigns: 12,
  ad_groups: 12,
  asset_groups: 12,
  age_ranges: 12,
  gender: 12,
  audiences: 12,
  // Top & Flop tables (limités)
  topflop_terms: 7,
  topflop_terms_top: 5,
  topflop_terms_flop: 5,
  topflop_products: 5,
  topflop_products_top: 5,
  topflop_products_flop: 5,
  topflop_categories: 5,
  topflop_categories_top: 5,
  topflop_categories_flop: 5,
  topflop_itemgroups: 5,
  topflop_itemgroups_top: 5,
  topflop_itemgroups_flop: 5,
  // Homepage tables
  homepage_analytics: 12,
  homepage_platforms: 12,
  homepage_consolide: 12,
  // Microsoft Ads tables
  msads_results: 12,
  msads_campaigns: 12,
  msads_adgroups: 12,
  msads_campaign_types: 12,
  msads_devices: 12,
  msads_networks: 12,
  msads_search_terms: 12,
  /**
   * Ces deux-là manquaient, et le repli `|| 10` de `applyTemplateToAllComponents` les plafonnait
   * donc à dix colonnes là où les sept autres tables Microsoft Ads en acceptent douze.
   *
   * Le symptôme se lisait « le template E-commerce ne s'applique pas à Origine géographique et
   * Jours de la semaine » : il s'appliquait, mais sa onzième et dernière métrique — le **COS** —
   * tombait à la troncature, sur les deux seules tables de la plateforme à la perdre. Toutes les
   * tables Microsoft Ads ont `panier_moyen` dans leurs métriques disponibles, ce qui porte le
   * template à onze colonnes : un plafond de dix ne pouvait pas le tenir.
   *
   * ⚠️ **Un composant déclaré dans `PLATFORM_COMPONENTS` doit avoir sa ligne ici.** Sans elle, le
   * plafond ne vaut pas celui de ses voisins et un template se tronque en silence — un composant
   * qui tronque rend un tableau plausible, c'est ce qui rend l'oubli coûteux à repérer.
   */
  msads_geography: 12,
  msads_weekdays: 12,
  // Facebook Ads tables
  fbads_results: 12,
  /**
   * Deux places de plus depuis l'arrivée de la couverture et de la répétition.
   *
   * La sélection par défaut de ces écrans occupait **exactement** le plafond : les deux
   * nouvelles métriques apparaissaient bien dans « Métriques disponibles », mais grisées et
   * impossibles à cocher sans en retirer deux autres d'abord. Un catalogue qui s'enrichit sans
   * que le plafond suive n'ajoute rien de visible.
   *
   * `fbads_weekdays` garde 12 : il ne propose pas ces deux métriques, Meta n'ayant pas de
   * découpage par jour de semaine.
   */
  fbads_campaigns: 14,
  fbads_devices: 14,
  fbads_weekdays: 12,
  fbads_age: 14,
  fbads_gender: 14,
  fbads_placements: 14,
  fbads_platform: 14,
  fbads_adsets: 12,
  fbads_ads: 12,
  fbads_engagement: 12,
  // TikTok Ads tables
  tiktok_results: 13,
  tiktok_campaigns: 10,
  // Pinterest Ads tables
  pinterest_results: 10,
  // ChatGPT Ads tables
  awin_results: 9,
  awin_publishers: 9,
  // Six : le tableau des campagnes n'a ni clics, ni CPC, ni taux de conversion.
  awin_campaigns: 6,
  /*
   * Douze, comme Microsoft Ads et Google Ads.
   *
   * Huit jusqu'ici, au motif que l'API ChatGPT Ads n'exposait pas la valeur des
   * conversions. **C'était faux** : `/conversions/insights` rend
   * `order_created_attributed_sales` avec sa devise, aux trois niveaux d'agrégation —
   * vérifié contre l'API en service. Panier moyen, Valeur Conv., COS, ROAS et ROI sont
   * donc calculables, et le template E-commerce en occupe onze.
   */
  chatgpt_results: 12,
  chatgpt_campaigns: 12,
  chatgpt_ad_groups: 12,
  chatgpt_ads: 12,
  // Piano Analytics tables
  piano_results: 12,
  piano_channels: 12,
  piano_geography: 12,
  piano_devices: 12,
  piano_browsers: 12,
  piano_languages: 12,
  piano_weekdays: 12,
  piano_pages: 12,
  // Matomo Analytics tables
  matomo_results: 12,
  matomo_channels: 12,
  matomo_geography: 12,
  matomo_devices: 12,
  matomo_browsers: 12,
  matomo_languages: 12,
  matomo_weekdays: 12,
  matomo_landing_pages: 12,
  matomo_ecommerce_products: 7,
  matomo_events: 4,
  // Google Analytics 4 tables
  ga4_results: 12,
  ga4_channels: 12,
  ga4_geography: 12,
  ga4_devices: 12,
  ga4_pages: 12,
  ga4_weekdays: 12
};

// Métriques standards disponibles pour personnalisation
export const GOOGLE_ADS_METRICS = [
  { key: 'clics', label: 'Clics' },
  { key: 'impressions', label: 'Impressions' },
  { key: 'ctr', label: 'CTR' },
  { key: 'cpc', label: 'CPC' },
  { key: 'cout', label: 'Coût' },
  { key: 'taux_conv', label: 'Taux conv.' },
  { key: 'conv', label: 'Conv.' },
  { key: 'cpa', label: 'CPA' },
  { key: 'valeur_conv', label: 'Valeur Conv.' },
  { key: 'cos', label: 'COS' },
  { key: 'panier_moyen', label: 'Panier moyen' },
  { key: 'roas', label: 'ROAS' },
  { key: 'roi', label: 'ROI' },
  /**
   * Vidéo TrueView. Le taux de vue se rapporte aux impressions **vidéo**, pas aux impressions
   * totales : sur un compte réel, 42 vues pour 164 impressions vidéo au sein de 772 impressions,
   * soit 25,6 % et non 5,4 %. Le backend renvoie donc `views`, `videoImpressions` et `viewCost`,
   * toutes sommables, dont ces deux ratios se dérivent à n'importe quel niveau d'agrégation.
   *
   * Réservées aux écrans dont la requête porte ces champs : l'API les refuse sur
   * `shopping_performance_view` et `asset_group`, où elles donneraient des colonnes vides.
   */
  { key: 'views', label: 'Vues' },
  { key: 'vtr', label: 'Taux de vue' },
  { key: 'cpv', label: 'CPV' },
  // Métriques spécifiques pour GA4
  { key: 'sessions', label: 'Sessions' },
  { key: 'users', label: 'Utilisateurs' },
  { key: 'pageViews', label: 'Pages vues' },
  { key: 'bounceRate', label: 'Taux rebond' },
  // Métriques spécifiques Piano Analytics
  { key: 'visites', label: 'Visites' },
  { key: 'visiteurs', label: 'Visiteurs' },
  { key: 'rebond', label: '% Rebond' },
  { key: 'pages_vues', label: 'Pages vues' },
  { key: 'duree_moy', label: 'Durée moyenne' },
  { key: 'pages_visite', label: 'Pages/Visite' },
  { key: 'events', label: 'Événements' },
  { key: 'actions_par_event', label: 'Actions/Événement' }
];

// Métriques disponibles par composant (extensible)
export const COMPONENT_AVAILABLE_METRICS = {
  /*
   * Les deux ventilations du dashboard PrestaShop.
   *
   * ⚠️ **Sans ces entrées, `RepartitionChart` n'afficherait aucune colonne** : il ne rend que ce
   * que `getComponentMetrics` déclare, et un `componentId` inconnu rend une liste vide. Le
   * tableau s'afficherait donc avec son camembert et pas une seule valeur, sans qu'aucune erreur
   * ne le signale.
   *
   * Trois colonnes seulement, et c'est tout ce qu'une boutique sait : ni visite, ni rebond, ni
   * page vue. Les noms sont ceux de `COLONNES` dans `RepartitionChart`.
   */
  prestashop_etats: ['transactions', 'chiffre_affaires', 'panier_moyen'],
  prestashop_produits: ['quantite', 'transactions', 'chiffre_affaires', 'prix_moyen', 'panier_moyen'],
  results: ['clics', 'impressions', 'ctr', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'panier_moyen', 'roas', 'roi', 'views', 'vtr', 'cpv'],
  comparatif: ['clics', 'impressions', 'ctr', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'panier_moyen', 'roas', 'roi', 'views', 'vtr', 'cpv'],
  topflop_terms: ['clics', 'impressions', 'ctr', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'panier_moyen', 'roas', 'roi'],
  topflop_terms_top: ['clics', 'impressions', 'ctr', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'panier_moyen', 'roas', 'roi'],
  topflop_terms_flop: ['clics', 'impressions', 'ctr', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'panier_moyen', 'roas', 'roi'],
  topflop_products: ['clics', 'impressions', 'ctr', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'panier_moyen', 'roas', 'roi'],
  topflop_products_top: ['clics', 'impressions', 'ctr', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'panier_moyen', 'roas', 'roi'],
  topflop_products_flop: ['clics', 'impressions', 'ctr', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'panier_moyen', 'roas', 'roi'],
  topflop_categories: ['clics', 'impressions', 'ctr', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'panier_moyen', 'roas', 'roi'],
  topflop_categories_top: ['clics', 'impressions', 'ctr', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'panier_moyen', 'roas', 'roi'],
  topflop_categories_flop: ['clics', 'impressions', 'ctr', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'panier_moyen', 'roas', 'roi'],
  topflop_itemgroups: ['clics', 'impressions', 'ctr', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'panier_moyen', 'roas', 'roi'],
  topflop_itemgroups_top: ['clics', 'impressions', 'ctr', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'panier_moyen', 'roas', 'roi'],
  topflop_itemgroups_flop: ['clics', 'impressions', 'ctr', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'panier_moyen', 'roas', 'roi'],
  networks: ['clics', 'impressions', 'ctr', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'panier_moyen', 'roas', 'roi', 'views', 'vtr', 'cpv'],
  devices: ['clics', 'impressions', 'ctr', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'panier_moyen', 'roas', 'roi', 'views', 'vtr', 'cpv'],
  weekdays: ['clics', 'impressions', 'ctr', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'panier_moyen', 'roas', 'roi', 'views', 'vtr', 'cpv'],
  geography: ['clics', 'impressions', 'ctr', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'panier_moyen', 'roas', 'roi', 'views', 'vtr', 'cpv'],
  campaign_types: ['clics', 'impressions', 'ctr', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'panier_moyen', 'roas', 'roi', 'views', 'vtr', 'cpv'],
  campaigns: ['clics', 'impressions', 'ctr', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'panier_moyen', 'roas', 'roi', 'views', 'vtr', 'cpv'],
  ad_groups: ['clics', 'impressions', 'ctr', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'panier_moyen', 'roas', 'roi', 'views', 'vtr', 'cpv'],
  asset_groups: ['clics', 'impressions', 'ctr', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'panier_moyen', 'roas', 'roi', 'views', 'vtr', 'cpv'],
  age_ranges: ['clics', 'impressions', 'ctr', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'panier_moyen', 'roas', 'roi', 'views', 'vtr', 'cpv'],
  gender: ['clics', 'impressions', 'ctr', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'panier_moyen', 'roas', 'roi', 'views', 'vtr', 'cpv'],
  audiences: ['clics', 'impressions', 'ctr', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'panier_moyen', 'roas', 'roi', 'views', 'vtr', 'cpv'],
  keywords: ['impressions', 'ctr', 'clics', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'roas', 'roi'],
  keywords_cpc_tiers: ['clics', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'roas', 'roi'],
  keywords_match_type: ['impressions', 'ctr', 'clics', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'roas', 'roi'],
  keywords_quality_score: ['impressions', 'ctr', 'clics', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'roas', 'roi'],
  // Search terms tables
  search_terms: ['impressions', 'ctr', 'clics', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'roas', 'roi'],
  search_terms_combined: ['impressions', 'ctr', 'clics', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'roas', 'roi'],
  search_terms_cpc_tiers: ['clics', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'roas', 'roi'],
  search_terms_ngram: ['impressions', 'ctr', 'clics', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'roas', 'roi'],
  // Landing pages table
  landing_pages: ['impressions', 'ctr', 'clics', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'roas', 'roi'],
  // Shopping products dashboard
  shopping_products: ['impressions', 'ctr', 'clics', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'roas', 'roi'],
  // Performance Max dashboard
  pmax: ['impressions', 'ctr', 'clics', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'roas', 'roi'],
  // Homepage tables - sessions pour analytics, clics pour platforms
  homepage_analytics: ['sessions', 'cout', 'taux_conv', 'conv', 'panier_moyen', 'valeur_conv', 'cpa', 'cos', 'roas', 'roi'],
  homepage_platforms: ['clics', 'cout', 'taux_conv', 'conv', 'panier_moyen', 'valeur_conv', 'cpa', 'cos', 'roas', 'roi'],
  // Le consolidé lit les mêmes lignes que le tableau Analytics, mais sa colonne se règle
  // à part : on n'y cherche pas forcément les mêmes chiffres que dans la section Analytics.
  homepage_consolide: ['sessions', 'cout', 'taux_conv', 'conv', 'panier_moyen', 'valeur_conv', 'cpa', 'cos', 'roas', 'roi'],
  // Microsoft Ads tables
  msads_results: ['impressions', 'ctr', 'clics', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'panier_moyen', 'roas', 'roi'],
  msads_campaigns: ['impressions', 'ctr', 'clics', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'panier_moyen', 'roas', 'roi'],
  msads_adgroups: ['impressions', 'ctr', 'clics', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'panier_moyen', 'roas', 'roi'],
  msads_campaign_types: ['impressions', 'ctr', 'clics', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'panier_moyen', 'roas', 'roi'],
  msads_devices: ['impressions', 'ctr', 'clics', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'panier_moyen', 'roas', 'roi'],
  msads_networks: ['impressions', 'ctr', 'clics', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'panier_moyen', 'roas', 'roi'],
  msads_search_terms: ['impressions', 'ctr', 'clics', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'panier_moyen', 'roas', 'roi'],
  msads_geography: ['impressions', 'ctr', 'clics', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'panier_moyen', 'roas', 'roi'],
  msads_weekdays: ['impressions', 'ctr', 'clics', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'panier_moyen', 'roas', 'roi'],
  // Facebook Ads tables
  // `couverture` et `repetition` sont proposées partout où Meta sait les rendre : chaque ligne
  // porte alors une valeur dédupliquée **dans son segment**. Leur total reste vide — la somme de
  // segments qui se recouvrent n'a pas de sens, une même personne figurant dans plusieurs.
  // `fbads_weekdays` en est seul privé : ce tableau replie des journées côté Kokpit, Meta n'ayant
  // pas de découpage par jour de semaine, et replier des couvertures revient à les additionner.
  fbads_devices: ['clics', 'impressions', 'couverture', 'repetition', 'ctr', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'transactions', 'cos', 'roas', 'roi', 'interactions', 'cout_par_interaction', 'reactions', 'cout_par_reaction', 'commentaires', 'enregistrements', 'partages', 'jaime_facebook'],
  fbads_weekdays: ['clics', 'impressions', 'ctr', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'transactions', 'cos', 'roas', 'roi', 'interactions', 'cout_par_interaction', 'reactions', 'cout_par_reaction', 'commentaires', 'enregistrements', 'partages', 'jaime_facebook'],
  fbads_age: ['clics', 'impressions', 'couverture', 'repetition', 'ctr', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'transactions', 'cos', 'roas', 'roi', 'interactions', 'cout_par_interaction', 'reactions', 'cout_par_reaction', 'commentaires', 'enregistrements', 'partages', 'jaime_facebook'],
  fbads_gender: ['clics', 'impressions', 'couverture', 'repetition', 'ctr', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'transactions', 'cos', 'roas', 'roi', 'interactions', 'cout_par_interaction', 'reactions', 'cout_par_reaction', 'commentaires', 'enregistrements', 'partages', 'jaime_facebook'],
  fbads_placements: ['clics', 'impressions', 'couverture', 'repetition', 'ctr', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'transactions', 'cos', 'roas', 'roi', 'interactions', 'cout_par_interaction', 'reactions', 'cout_par_reaction', 'commentaires', 'enregistrements', 'partages', 'jaime_facebook'],
  fbads_platform: ['clics', 'impressions', 'couverture', 'repetition', 'ctr', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'transactions', 'cos', 'roas', 'roi', 'interactions', 'cout_par_interaction', 'reactions', 'cout_par_reaction', 'commentaires', 'enregistrements', 'partages', 'jaime_facebook'],
  fbads_adsets: ['clics', 'impressions', 'couverture', 'repetition', 'ctr', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'transactions', 'cos', 'roas', 'roi', 'interactions', 'cout_par_interaction', 'reactions', 'cout_par_reaction', 'commentaires', 'enregistrements', 'partages', 'jaime_facebook'],
  // Les deux coûts accompagnent partout ailleurs `interactions` et `reactions` ; ils
  // manquaient ici seuls. Le template Engagement les proposait donc sans que ce tableau
  // puisse les afficher — un levier d'engagement s'y lisait en volume, jamais en coût.
  // `roas` et `roi` manquaient au seul tableau des annonces, alors qu'ils sont proposés sur
  // tous les autres écrans Meta. Ajoutés ici **et** rendus par `AdsTable` : une métrique
  // offerte à la configuration sans cellule pour l'afficher se coche sans rien produire.
  fbads_ads: ['clics', 'impressions', 'couverture', 'repetition', 'ctr', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'transactions', 'cos', 'roas', 'roi', 'interactions', 'cout_par_interaction', 'reactions', 'cout_par_reaction', 'videos_vues', 'taux_videos_vues', 'commentaires', 'enregistrements', 'partages', 'jaime_facebook'],
  fbads_engagement: ['clics', 'impressions', 'ctr', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'roas', 'roi', 'interactions', 'cout_par_interaction', 'reactions', 'cout_par_reaction', 'commentaires', 'enregistrements', 'partages', 'jaime_facebook'],
  fbads_results: ['clics', 'impressions', 'couverture', 'repetition', 'ctr', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'transactions', 'cos', 'panier_moyen', 'roas', 'roi', 'interactions', 'cout_par_interaction', 'reactions', 'cout_par_reaction', 'commentaires', 'enregistrements', 'partages', 'jaime_facebook'],
  // Les métriques vidéo ne sont renvoyées que par `getCampaignMetrics` et `getAdMetrics` :
  // les proposer sur les autres écrans Meta donnerait des colonnes vides, l'API ne les
  // fournissant pas à ces niveaux.
  // `couverture` et `repetition` ne sont proposées que sur ces deux écrans Meta : les autres
  // tableaux (plateforme, appareil, âge, genre, placement, jour) reposent sur des breakdowns
  // où une même personne apparaît dans plusieurs lignes, et où aucun total dédupliqué n'existe.
  fbads_campaigns: ['clics', 'impressions', 'couverture', 'repetition', 'ctr', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'transactions', 'cos', 'roas', 'roi', 'interactions', 'cout_par_interaction', 'reactions', 'cout_par_reaction', 'videos_vues', 'taux_videos_vues', 'commentaires', 'enregistrements', 'partages', 'jaime_facebook'],
  // TikTok Ads tables
  tiktok_results: ['impressions', 'ctr', 'clics', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'panier_moyen', 'cos', 'roas', 'roi'],
  tiktok_campaigns: ['clics', 'impressions', 'ctr', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos'],
  // Pinterest Ads tables
  pinterest_results: ['impressions', 'ctr', 'clics', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'panier_moyen', 'valeur_conv', 'cos', 'roas', 'roi'],
  // ChatGPT Ads tables
  // Les conversions viennent d'un point d'entrée dédié, sans valeur de conversion :
  // ROAS, COS, panier moyen et valeur de conversion n'ont aucune source côté OpenAI et
  // afficheraient des zéros permanents.
  awin_results: ['clics', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'panier_moyen', 'valeur_conv', 'cos'],
  awin_publishers: ['clics', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'panier_moyen', 'valeur_conv', 'cos'],
  /**
   * Pas de clics ici, et ce n'est pas un oubli.
   *
   * Le rapport de trafic d'Awin se ventile par **éditeur**, jamais par campagne. Clics, CPC
   * et taux de conversion n'ont donc aucune source à ce niveau et resteraient vides en
   * permanence : les proposer ferait lire une mesure absente comme une mesure nulle.
   */
  awin_campaigns: ['cout', 'conv', 'cpa', 'panier_moyen', 'valeur_conv', 'cos'],
  /*
   * Les cinq mesures marchandes rejoignent le catalogue — voir `COMPONENT_METRICS_LIMITS`.
   *
   * ⚠️ **Elles restent hors du jeu par défaut**, qui garde les huit colonnes d'acquisition.
   * `order_created_attributed_sales` ne vaut que pour un annonceur qui déclare des ventes :
   * sur un compte de génération de contacts il vaut 0, et cinq colonnes de zéros par défaut
   * se liraient comme un constat de vente nulle. Le template E-commerce les active d'un coup
   * pour qui en a l'usage.
   */
  chatgpt_results: ['impressions', 'ctr', 'clics', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'panier_moyen', 'valeur_conv', 'cos', 'roas', 'roi'],
  chatgpt_campaigns: ['impressions', 'ctr', 'clics', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'panier_moyen', 'valeur_conv', 'cos', 'roas', 'roi'],
  chatgpt_ad_groups: ['impressions', 'ctr', 'clics', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'panier_moyen', 'valeur_conv', 'cos', 'roas', 'roi'],
  chatgpt_ads: ['impressions', 'ctr', 'clics', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'panier_moyen', 'valeur_conv', 'cos', 'roas', 'roi'],
  // Piano Analytics tables
  /**
   * ⚠️ **Les trois solutions de mesure portent la même liste, dans le même ordre.**
   *
   * Un même nom de métrique doit désigner la même chose chez GA4, Piano et Matomo, faute de
   * quoi on compare des tableaux qui se ressemblent sans dire la même chose. Les trois
   * dashboards rendent donc les mêmes champs (`conversions`, `transactions`, `totalRevenue`,
   * `transactionRate`, `actionsPerVisit`…), et c'est ce qui permet à cette liste d'être
   * commune.
   *
   * **Ajouter une métrique à GA4, c'est lui chercher son équivalent chez les deux autres** et
   * l'ajouter aux trois listes. Une métrique posée sur une seule plateforme laisse une colonne
   * vide chez les autres, ce qui se lit comme une panne.
   *
   * Seule exception, documentée : **Matomo n'a pas « Utilisateurs »**. Son API refuse
   * `nb_uniq_visitors` sur une période glissante — un réglage d'instance désactivé par défaut —
   * et la somme des valeurs quotidiennes compterait plusieurs fois un visiteur revenu. La
   * métrique est donc absente de sa liste plutôt que fausse.
   *
   * **Les tableaux Canaux et Géographie portent la même liste que Résultats**, pour les trois.
   * Matomo y garde « Utilisateurs » : ses rapports de répartition rendent
   * `sum_daily_nb_uniq_visitors`, là où la vue d'ensemble refuse la mesure sur une plage.
   */
  piano_results: ['visites', 'visiteurs', 'rebond', 'pages_vues', 'pages_visite', 'duree_moy', 'taux_conv', 'conv', 'panier_moyen', 'transactions', 'chiffre_affaires', 'taux_transaction'],
  piano_channels: ['visites', 'visiteurs', 'rebond', 'pages_vues', 'pages_visite', 'duree_moy', 'taux_conv', 'conv', 'panier_moyen', 'transactions', 'chiffre_affaires', 'taux_transaction'],
  piano_geography: ['visites', 'visiteurs', 'rebond', 'pages_vues', 'pages_visite', 'duree_moy', 'taux_conv', 'conv', 'panier_moyen', 'transactions', 'chiffre_affaires', 'taux_transaction'],
  piano_devices: ['visites', 'visiteurs', 'rebond', 'pages_vues', 'pages_visite', 'duree_moy', 'taux_conv', 'conv', 'panier_moyen', 'transactions', 'chiffre_affaires', 'taux_transaction'],
  piano_browsers: ['visites', 'visiteurs', 'rebond', 'pages_vues', 'pages_visite', 'duree_moy', 'taux_conv', 'conv', 'panier_moyen', 'transactions', 'chiffre_affaires', 'taux_transaction'],
  piano_languages: ['visites', 'visiteurs', 'rebond', 'pages_vues', 'pages_visite', 'duree_moy', 'taux_conv', 'conv', 'panier_moyen', 'transactions', 'chiffre_affaires', 'taux_transaction'],
  piano_weekdays: ['visites', 'visiteurs', 'rebond', 'pages_vues', 'pages_visite', 'duree_moy', 'taux_conv', 'conv', 'panier_moyen', 'transactions', 'chiffre_affaires', 'taux_transaction'],
  /*
   * **Les pages n'affichent ni transactions ni taux de conversion par défaut.**
   *
   * Piano attribue la transaction à la page où elle se produit : vérifié, les 6 105 transactions
   * du mois portent toutes sur `/client/confirmation-commande/`, et zéro sur les 49 autres pages.
   * Une colonne Transactions serait donc un mur de zéros avec une seule ligne à 6 105 — des
   * chiffres justes qui se lisent comme un défaut de collecte.
   *
   * Le chiffre d'affaires, lui, est bien réparti sur les pages traversées par la visite, et reste
   * affiché. Les colonnes retirées restent disponibles dans la modale de configuration.
   */
  piano_pages: ['visites', 'visiteurs', 'rebond', 'pages_vues', 'pages_visite', 'duree_moy', 'chiffre_affaires'],
  // Matomo Analytics tables
  matomo_results: ['visites', 'visiteurs', 'rebond', 'pages_vues', 'pages_visite', 'duree_moy', 'taux_conv', 'conv', 'panier_moyen', 'transactions', 'chiffre_affaires', 'taux_transaction'],
  matomo_channels: ['visites', 'visiteurs', 'rebond', 'pages_vues', 'pages_visite', 'duree_moy', 'taux_conv', 'conv', 'panier_moyen', 'transactions', 'chiffre_affaires', 'taux_transaction'],
  matomo_geography: ['visites', 'visiteurs', 'rebond', 'pages_vues', 'pages_visite', 'duree_moy', 'taux_conv', 'conv', 'panier_moyen', 'transactions', 'chiffre_affaires', 'taux_transaction'],
  matomo_devices: ['visites', 'visiteurs', 'rebond', 'pages_vues', 'pages_visite', 'duree_moy', 'taux_conv', 'conv', 'panier_moyen', 'transactions', 'chiffre_affaires', 'taux_transaction'],
  matomo_browsers: ['visites', 'visiteurs', 'rebond', 'pages_vues', 'pages_visite', 'duree_moy', 'taux_conv', 'conv', 'panier_moyen', 'transactions', 'chiffre_affaires', 'taux_transaction'],
  /*
   * **Matomo n'a pas d'objectifs par langue**, contrairement aux appareils et aux navigateurs :
   * ni `getLanguage`, ni `getLanguageCode`, ni `API.getProcessedReport` ne rendent d'objet
   * `goals` — vérifié. Transactions, chiffre d'affaires et taux de conversion sont donc retirés
   * du jeu par défaut : une colonne de zéros se lit comme une absence de ventes, pas comme une
   * absence de mesure. Elles restent accessibles dans la modale, pour qui veut constater.
   */
  matomo_languages: ['visites', 'visiteurs', 'rebond', 'pages_vues', 'pages_visite', 'duree_moy'],
  matomo_weekdays: ['visites', 'visiteurs', 'rebond', 'pages_vues', 'pages_visite', 'duree_moy', 'taux_conv', 'conv', 'panier_moyen', 'transactions', 'chiffre_affaires', 'taux_transaction'],
  /*
   * **Matomo n'attribue pas les commandes à la page d'entrée** mais à chaque page vue pendant
   * la visite : la somme des lignes dépassait de cinq fois le chiffre d'affaires réel du site.
   * Les mesures marchandes sont donc écartées du jeu par défaut — voir `getLandingPages`.
   */
  matomo_landing_pages: ['visites', 'visiteurs', 'rebond', 'pages_vues', 'pages_visite', 'duree_moy'],
  matomo_ecommerce_products: ['transactions', 'chiffre_affaires', 'panier_moyen'],
  /*
   * **Le rapport d'événements ne porte aucune conversion** : `taux_conv` y était déclaré sans
   * rien derrière. En revanche il rend les visiteurs uniques et les valeurs d'événement, que
   * l'écran n'affichait pas.
   *
   * Les valeurs d'événement ont été retirées : sur les sites observés, une seule catégorie sur
   * vingt-deux en porte une, et vingt et une colonnes de zéros se lisent comme un constat.
   */
  matomo_events: ['events', 'visites', 'visiteurs', 'actions_par_event'],
  // Google Analytics 4 tables
  ga4_results: ['visites', 'visiteurs', 'rebond', 'pages_vues', 'pages_visite', 'duree_moy', 'taux_conv', 'conv', 'panier_moyen', 'transactions', 'chiffre_affaires', 'taux_transaction'],
  ga4_channels: ['visites', 'visiteurs', 'rebond', 'pages_vues', 'pages_visite', 'duree_moy', 'taux_conv', 'conv', 'panier_moyen', 'transactions', 'chiffre_affaires', 'taux_transaction'],
  ga4_geography: ['visites', 'visiteurs', 'rebond', 'pages_vues', 'pages_visite', 'duree_moy', 'taux_conv', 'conv', 'panier_moyen', 'transactions', 'chiffre_affaires', 'taux_transaction'],
  ga4_devices: ['visites', 'visiteurs', 'rebond', 'pages_vues', 'pages_visite', 'duree_moy', 'taux_conv', 'conv', 'panier_moyen', 'transactions', 'chiffre_affaires', 'taux_transaction'],
  ga4_pages: ['visites', 'visiteurs', 'rebond', 'pages_vues', 'pages_visite', 'duree_moy', 'taux_conv', 'conv', 'panier_moyen', 'transactions', 'chiffre_affaires', 'taux_transaction'],
  ga4_weekdays: ['visites', 'visiteurs', 'rebond', 'pages_vues', 'pages_visite', 'duree_moy', 'transactions', 'chiffre_affaires', 'taux_transaction', 'panier_moyen', 'conv', 'taux_conv']
};

// Métriques par défaut par composant (ordre initial)
// Les composants Top & Flop ont 5 métriques par défaut, les autres ont les 10
export const COMPONENT_DEFAULT_METRICS = {
  /*
   * Les deux ventilations du dashboard PrestaShop.
   *
   * ⚠️ **Sans ces entrées, `RepartitionChart` n'afficherait aucune colonne** : il ne rend que ce
   * que `getComponentMetrics` déclare, et un `componentId` inconnu rend une liste vide. Le
   * tableau s'afficherait donc avec son camembert et pas une seule valeur, sans qu'aucune erreur
   * ne le signale.
   *
   * Trois colonnes seulement, et c'est tout ce qu'une boutique sait : ni visite, ni rebond, ni
   * page vue. Les noms sont ceux de `COLONNES` dans `RepartitionChart`.
   */
  prestashop_etats: ['transactions', 'chiffre_affaires', 'panier_moyen'],
  prestashop_produits: ['quantite', 'chiffre_affaires', 'prix_moyen'],
  results: ['clics', 'cpc', 'cout', 'taux_conv', 'conv', 'valeur_conv', 'panier_moyen', 'cos'],
  /**
   * Dashboard comparatif : le template E-commerce par défaut, repris mot pour mot de
   * `METRICS_TEMPLATES.gads[0].metrics`. Les deux doivent rester identiques — un utilisateur
   * qui applique « E-commerce » sur un écran fraîchement ouvert ne doit voir aucun changement.
   */
  comparatif: ['impressions', 'ctr', 'clics', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'panier_moyen', 'valeur_conv', 'cos'],
  topflop_terms: ['clics', 'taux_conv', 'conv', 'cos', 'cpc', 'cout'],
  topflop_terms_top: ['clics', 'taux_conv', 'conv', 'cos'],
  topflop_terms_flop: ['clics', 'cpc', 'cout', 'cos'],
  topflop_products: ['clics', 'taux_conv', 'conv', 'cpa', 'cos'],
  topflop_products_top: ['clics', 'taux_conv', 'conv', 'cos'],
  topflop_products_flop: ['clics', 'cpc', 'cout', 'cos'],
  topflop_categories: ['clics', 'taux_conv', 'conv', 'cos'],
  topflop_categories_top: ['clics', 'taux_conv', 'conv', 'cos'],
  topflop_categories_flop: ['clics', 'cpc', 'cout', 'cos'],
  topflop_itemgroups: ['clics', 'taux_conv', 'conv', 'cos'],
  topflop_itemgroups_top: ['clics', 'taux_conv', 'conv', 'cos'],
  topflop_itemgroups_flop: ['clics', 'cpc', 'cout', 'cos'],
  networks: ['clics', 'impressions', 'ctr', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'roas', 'roi'],
  devices: ['clics', 'impressions', 'ctr', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'roas', 'roi'],
  weekdays: ['clics', 'impressions', 'ctr', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'roas', 'roi'],
  geography: ['clics', 'impressions', 'ctr', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'roas', 'roi'],
  campaign_types: ['clics', 'impressions', 'ctr', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'roas', 'roi'],
  campaigns: ['clics', 'impressions', 'ctr', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'roas', 'roi'],
  ad_groups: ['clics', 'impressions', 'ctr', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'roas', 'roi'],
  asset_groups: ['clics', 'impressions', 'ctr', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'roas', 'roi'],
  age_ranges: ['clics', 'impressions', 'ctr', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'roas', 'roi'],
  gender: ['clics', 'impressions', 'ctr', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'roas', 'roi'],
  audiences: ['clics', 'impressions', 'ctr', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'roas', 'roi'],
  keywords: ['impressions', 'ctr', 'clics', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'roas', 'roi'],
  keywords_cpc_tiers: ['clics', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'roas', 'roi'],
  keywords_match_type: ['impressions', 'ctr', 'clics', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'roas', 'roi'],
  keywords_quality_score: ['impressions', 'ctr', 'clics', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'roas', 'roi'],
  // Search terms tables
  search_terms: ['impressions', 'ctr', 'clics', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'roas', 'roi'],
  search_terms_combined: ['impressions', 'ctr', 'clics', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'roas', 'roi'],
  search_terms_cpc_tiers: ['clics', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'roas', 'roi'],
  search_terms_ngram: ['impressions', 'ctr', 'clics', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'roas', 'roi'],
  // Landing pages table
  landing_pages: ['impressions', 'ctr', 'clics', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'roas', 'roi'],
  // Shopping products dashboard
  shopping_products: ['impressions', 'ctr', 'clics', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'roas', 'roi'],
  // Performance Max dashboard
  pmax: ['impressions', 'ctr', 'clics', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'roas', 'roi'],
  // Homepage tables
  homepage_analytics: ['sessions', 'cout', 'taux_conv', 'conv', 'panier_moyen', 'valeur_conv', 'cpa', 'cos'],
  homepage_platforms: ['clics', 'cout', 'taux_conv', 'conv', 'panier_moyen', 'valeur_conv', 'cpa', 'cos'],
  homepage_consolide: ['sessions', 'cout', 'taux_conv', 'conv', 'panier_moyen', 'valeur_conv', 'cpa', 'cos'],
  // Microsoft Ads tables
  msads_results: ['impressions', 'ctr', 'clics', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'roas', 'roi'],
  msads_campaigns: ['impressions', 'ctr', 'clics', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'roas', 'roi'],
  msads_adgroups: ['impressions', 'ctr', 'clics', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'roas', 'roi'],
  msads_campaign_types: ['impressions', 'ctr', 'clics', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'roas', 'roi'],
  msads_devices: ['impressions', 'ctr', 'clics', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'roas', 'roi'],
  msads_networks: ['impressions', 'ctr', 'clics', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'roas', 'roi'],
  msads_search_terms: ['impressions', 'ctr', 'clics', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'roas', 'roi'],
  msads_geography: ['impressions', 'ctr', 'clics', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'roas', 'roi'],
  msads_weekdays: ['impressions', 'ctr', 'clics', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'roas', 'roi'],
  // Facebook Ads tables
  fbads_devices: ['clics', 'impressions', 'ctr', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'roas', 'roi'],
  fbads_weekdays: ['clics', 'impressions', 'ctr', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'roas', 'roi'],
  fbads_age: ['clics', 'impressions', 'ctr', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'roas', 'roi'],
  fbads_gender: ['clics', 'impressions', 'ctr', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'roas', 'roi'],
  fbads_placements: ['clics', 'impressions', 'ctr', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'roas', 'roi'],
  fbads_platform: ['clics', 'impressions', 'ctr', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'roas', 'roi'],
  fbads_adsets: ['clics', 'impressions', 'ctr', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos'],
  fbads_ads: ['clics', 'impressions', 'ctr', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos'],
  fbads_engagement: ['clics', 'impressions', 'ctr', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'roas', 'roi'],
  fbads_results: ['clics', 'cpc', 'cout', 'taux_conv', 'conv', 'valeur_conv', 'panier_moyen', 'cos', 'roas', 'roi'],
  fbads_campaigns: ['clics', 'impressions', 'ctr', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos', 'roas', 'roi'],
  // TikTok Ads tables
  tiktok_results: ['impressions', 'ctr', 'clics', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'panier_moyen', 'cos', 'roas', 'roi'],
  tiktok_campaigns: ['clics', 'impressions', 'ctr', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'valeur_conv', 'cos'],
  // Pinterest Ads tables
  pinterest_results: ['impressions', 'ctr', 'clics', 'cpc', 'cout', 'taux_conv', 'conv', 'panier_moyen', 'valeur_conv', 'cos'],
  // ChatGPT Ads tables
  /**
   * Quatre cartes seulement : l'entonnoir d'un programme d'affiliation, du trafic apporté à
   * ce qu'il coûte. Les autres mesures — CA, COS, CPA, panier moyen — restent disponibles
   * dans la configuration, mais les afficher toutes réduit chaque carte à une vignette et
   * noie les quatre chiffres qu'on vient réellement lire.
   */
  /**
   * Le dashboard s'ouvre sur le template **E-commerce** : ces colonnes sont exactement
   * les siennes. Les deux listes doivent rester identiques — un défaut qui différerait du
   * template afficherait autre chose que ce que le sélecteur annonce.
   */
  awin_results: ['clics', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'panier_moyen', 'valeur_conv', 'cos'],
  awin_publishers: ['clics', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'panier_moyen', 'valeur_conv', 'cos'],
  /**
   * Pas de clics ici, et ce n'est pas un oubli.
   *
   * Le rapport de trafic d'Awin se ventile par **éditeur**, jamais par campagne. Clics, CPC
   * et taux de conversion n'ont donc aucune source à ce niveau et resteraient vides en
   * permanence : les proposer ferait lire une mesure absente comme une mesure nulle.
   */
  awin_campaigns: ['cout', 'conv', 'cpa', 'panier_moyen', 'valeur_conv', 'cos'],
  chatgpt_results: ['impressions', 'ctr', 'clics', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa'],
  chatgpt_campaigns: ['impressions', 'ctr', 'clics', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa'],
  chatgpt_ad_groups: ['impressions', 'ctr', 'clics', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa'],
  chatgpt_ads: ['impressions', 'ctr', 'clics', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa'],
  // Piano Analytics tables
  piano_results: ['visites', 'visiteurs', 'rebond', 'pages_vues', 'pages_visite', 'duree_moy', 'taux_conv', 'conv', 'panier_moyen', 'transactions', 'chiffre_affaires', 'taux_transaction'],
  piano_channels: ['visites', 'visiteurs', 'rebond', 'pages_vues', 'pages_visite', 'duree_moy', 'taux_conv', 'conv', 'panier_moyen', 'transactions', 'chiffre_affaires', 'taux_transaction'],
  piano_geography: ['visites', 'visiteurs', 'rebond', 'pages_vues', 'pages_visite', 'duree_moy', 'taux_conv', 'conv', 'panier_moyen', 'transactions', 'chiffre_affaires', 'taux_transaction'],
  piano_devices: ['visites', 'visiteurs', 'rebond', 'pages_vues', 'pages_visite', 'duree_moy', 'taux_conv', 'conv', 'panier_moyen', 'transactions', 'chiffre_affaires', 'taux_transaction'],
  piano_browsers: ['visites', 'visiteurs', 'rebond', 'pages_vues', 'pages_visite', 'duree_moy', 'taux_conv', 'conv', 'panier_moyen', 'transactions', 'chiffre_affaires', 'taux_transaction'],
  piano_languages: ['visites', 'visiteurs', 'rebond', 'pages_vues', 'pages_visite', 'duree_moy', 'taux_conv', 'conv', 'panier_moyen', 'transactions', 'chiffre_affaires', 'taux_transaction'],
  piano_weekdays: ['visites', 'visiteurs', 'rebond', 'pages_vues', 'pages_visite', 'duree_moy', 'taux_conv', 'conv', 'panier_moyen', 'transactions', 'chiffre_affaires', 'taux_transaction'],
  piano_pages: ['visites', 'visiteurs', 'rebond', 'pages_vues', 'pages_visite', 'duree_moy', 'chiffre_affaires'],
  // Matomo Analytics tables
  matomo_results: ['visites', 'visiteurs', 'rebond', 'pages_vues', 'pages_visite', 'duree_moy', 'taux_conv', 'conv', 'panier_moyen', 'transactions', 'chiffre_affaires', 'taux_transaction'],
  matomo_channels: ['visites', 'visiteurs', 'rebond', 'pages_vues', 'pages_visite', 'duree_moy', 'taux_conv', 'conv', 'panier_moyen', 'transactions', 'chiffre_affaires', 'taux_transaction'],
  matomo_geography: ['visites', 'visiteurs', 'rebond', 'pages_vues', 'pages_visite', 'duree_moy', 'taux_conv', 'conv', 'panier_moyen', 'transactions', 'chiffre_affaires', 'taux_transaction'],
  matomo_devices: ['visites', 'visiteurs', 'rebond', 'pages_vues', 'pages_visite', 'duree_moy', 'taux_conv', 'conv', 'panier_moyen', 'transactions', 'chiffre_affaires', 'taux_transaction'],
  matomo_browsers: ['visites', 'visiteurs', 'rebond', 'pages_vues', 'pages_visite', 'duree_moy', 'taux_conv', 'conv', 'panier_moyen', 'transactions', 'chiffre_affaires', 'taux_transaction'],
  matomo_languages: ['visites', 'visiteurs', 'rebond', 'pages_vues', 'pages_visite', 'duree_moy'],
  matomo_weekdays: ['visites', 'visiteurs', 'rebond', 'pages_vues', 'pages_visite', 'duree_moy', 'taux_conv', 'conv', 'panier_moyen', 'transactions', 'chiffre_affaires', 'taux_transaction'],
  matomo_landing_pages: ['visites', 'visiteurs', 'rebond', 'pages_vues', 'pages_visite', 'duree_moy'],
  matomo_events: ['events', 'visites', 'visiteurs', 'actions_par_event'],
  // Google Analytics 4 tables
  ga4_results: ['visites', 'visiteurs', 'rebond', 'pages_vues', 'pages_visite', 'duree_moy', 'taux_conv', 'conv', 'panier_moyen', 'transactions', 'chiffre_affaires', 'taux_transaction'],
  ga4_channels: ['visites', 'visiteurs', 'rebond', 'pages_vues', 'pages_visite', 'duree_moy', 'taux_conv', 'conv', 'panier_moyen', 'transactions', 'chiffre_affaires', 'taux_transaction'],
  ga4_geography: ['visites', 'visiteurs', 'rebond', 'pages_vues', 'pages_visite', 'duree_moy', 'taux_conv', 'conv', 'panier_moyen', 'transactions', 'chiffre_affaires', 'taux_transaction'],
  ga4_devices: ['visites', 'visiteurs', 'rebond', 'pages_vues', 'pages_visite', 'duree_moy', 'taux_conv', 'conv', 'panier_moyen', 'transactions', 'chiffre_affaires', 'taux_transaction'],
  ga4_pages: ['visites', 'visiteurs', 'rebond', 'pages_vues', 'pages_visite', 'duree_moy', 'taux_conv', 'conv', 'panier_moyen', 'transactions', 'chiffre_affaires', 'taux_transaction'],
  ga4_weekdays: ['visites', 'visiteurs', 'rebond', 'pages_vues', 'pages_visite', 'duree_moy', 'transactions', 'chiffre_affaires', 'taux_transaction', 'panier_moyen', 'conv', 'taux_conv']
};

// Configuration initiale du graphique, par composant Résultats.
// Sans entrée ici, `PerformanceChart` garde son couple de repli (CTR en courbe,
// conversions en histogramme). Les clés sont celles du graphique — `clicks`,
// `conversions` — et non celles des métriques.
export const COMPONENT_DEFAULT_CHART_CONFIG = {
  chatgpt_results: { barMetric: 'conversions', lineMetric: 'clicks' },
  // Les ventes en barres, les clics en courbe : l'entonnoir de l'affiliation se lit ainsi,
  // le trafic apporté au regard de ce qu'il a converti.
  awin_results: { barMetric: 'conversions', lineMetric: 'clicks' }
};

// ===== TEMPLATES DE MÉTRIQUES =====
// Templates prédéfinis pour différents types d'activité

export const METRICS_TEMPLATES = {
  // Templates Google Ads
  // metrics = version courte, pour le composant Résultats
  // metricsExpanded = version longue, pour les autres composants
  // Les plafonds font foi dans `COMPONENT_METRICS_LIMITS` — 12 pour Résultats — et non ici :
  // les chiffres écrits dans ce commentaire étaient faux depuis longtemps.
  gads: [
    {
      id: 'ecommerce',
      name: 'E-commerce',
      description: 'Métriques orientées ventes et rentabilité',
      // `cpa` suit `conv`, comme dans `googleAdsCorrectOrder` et dans `metricsExpanded` :
      // le coût d'une conversion se lit à côté de leur nombre, pas ailleurs. Il manquait à
      // la seule version courte, si bien que le composant Résultats était le seul écran
      // e-commerce à ne pas le montrer.
      metrics: ['impressions', 'ctr', 'clics', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'panier_moyen', 'valeur_conv', 'cos'],
      metricsExpanded: ['impressions', 'ctr', 'clics', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'panier_moyen', 'valeur_conv', 'cos'],
      chartConfig: { barMetric: 'conversions', lineMetric: 'clicks' }
    },
    {
      id: 'leadgen',
      name: 'Lead Gen',
      description: 'Métriques orientées génération de leads',
      metrics: ['impressions', 'ctr', 'clics', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa'],
      metricsExpanded: ['impressions', 'ctr', 'clics', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa'],
      chartConfig: { barMetric: 'conversions', lineMetric: 'clicks' }
    }
  ],
  // Templates Microsoft Ads
  msads: [
    {
      id: 'ecommerce',
      name: 'E-commerce',
      description: 'Métriques orientées ventes et rentabilité',
      // `cpa` suit `conv`, comme dans `metricsExpanded` et comme chez Google Ads et Facebook Ads :
      // le coût d'une conversion se lit à côté de leur nombre. Il ne manquait qu'à cette version
      // courte, si bien que le composant Résultats était le seul écran e-commerce de Microsoft Ads
      // à ne pas le montrer — l'écart déjà corrigé sur les deux autres régies.
      metrics: ['impressions', 'ctr', 'clics', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'panier_moyen', 'valeur_conv', 'cos'],
      metricsExpanded: ['impressions', 'ctr', 'clics', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'panier_moyen', 'valeur_conv', 'cos'],
      chartConfig: { barMetric: 'conversions', lineMetric: 'clicks' }
    },
    {
      id: 'leadgen',
      name: 'Lead Gen',
      description: 'Métriques orientées génération de leads',
      metrics: ['clics', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa'],
      metricsExpanded: ['clics', 'impressions', 'ctr', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa']
    }
  ],
  // Templates Facebook Ads (avec interactions/réactions)
  fbads: [
    {
      id: 'ecommerce',
      name: 'E-commerce',
      description: 'Métriques orientées ventes et rentabilité',
      // Exactement les colonnes du template e-commerce de Google Ads, dans le même ordre :
      // un compte e-commerce se juge aux mêmes chiffres quelle que soit la régie, et deux
      // listes différentes obligeaient à relire les en-têtes en passant d'un écran à l'autre.
      // La version courte y gagne impressions, CTR et CPA, qu'elle seule n'avait pas.
      metrics: ['impressions', 'ctr', 'clics', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'panier_moyen', 'valeur_conv', 'cos'],
      metricsExpanded: ['impressions', 'ctr', 'clics', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'panier_moyen', 'valeur_conv', 'cos'],
      chartConfig: { barMetric: 'conversions', lineMetric: 'clicks' }
    },
    {
      id: 'leadgen',
      name: 'Lead Gen',
      description: 'Métriques orientées génération de leads',
      metrics: ['impressions', 'ctr', 'clics', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa'],
      metricsExpanded: ['impressions', 'ctr', 'clics', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa']
    },
    {
      id: 'engagement',
      name: 'Engagement',
      description: 'Métriques orientées interactions sociales',
      // Les deux listes doivent porter la même intention : `metrics` sert les cartes de
      // résultats, `metricsExpanded` les tableaux. `cout_par_reaction` n'avait été ajouté qu'à
      // la seconde, et le coût des réactions manquait donc aux seules cartes — là où l'on
      // regarde d'abord si un levier d'engagement coûte cher.
      metrics: ['impressions', 'ctr', 'clics', 'cpc', 'cout', 'interactions', 'cout_par_interaction', 'reactions', 'cout_par_reaction'],
      metricsExpanded: ['impressions', 'ctr', 'clics', 'cpc', 'cout', 'interactions', 'cout_par_interaction', 'reactions', 'cout_par_reaction'],
      chartConfig: { barMetric: 'clicks', lineMetric: 'coutParInteraction' }
    }
  ],
  // Templates Analytics (GA4, Piano, Matomo)
  analytics: [
    {
      id: 'ecommerce',
      name: 'E-commerce',
      description: 'Métriques orientées ventes',
      /**
       * L'ordre compte : c'est celui des colonnes et des cartes.
       *
       * Les personnes d'abord, puis leur comportement, puis ce qu'elles achètent — les
       * utilisateurs avant les sessions, les ventes en fin de liste.
       *
       * Matomo n'a pas « Utilisateurs » : son API refuse `nb_uniq_visitors` sur une période
       * glissante. `applyTemplateToAllComponents` filtre le modèle par les métriques
       * disponibles de chaque composant, la ligne disparaît donc d'elle-même chez lui sans
       * décaler les autres.
       */
      metrics: ['visiteurs', 'visites', 'rebond', 'pages_vues', 'pages_visite', 'duree_moy', 'taux_transaction', 'transactions', 'panier_moyen', 'chiffre_affaires'],
      metricsExpanded: ['visiteurs', 'visites', 'rebond', 'pages_vues', 'pages_visite', 'duree_moy', 'taux_transaction', 'transactions', 'panier_moyen', 'chiffre_affaires'],
      chartConfig: { barMetric: 'transactions', lineMetric: 'visits' }
    },
    {
      id: 'leadgen',
      name: 'Lead Gen',
      description: 'Métriques orientées génération de leads',
      metrics: ['visites', 'visiteurs', 'rebond', 'pages_vues', 'pages_visite', 'duree_moy', 'taux_conv', 'conv'],
      metricsExpanded: ['visites', 'visiteurs', 'rebond', 'pages_vues', 'pages_visite', 'duree_moy', 'taux_conv', 'conv'],
      chartConfig: { barMetric: 'conversions', lineMetric: 'visits' }
    },
    {
      id: 'content',
      name: 'Contenu',
      description: 'Métriques orientées engagement',
      metrics: ['visites', 'visiteurs', 'pages_vues', 'rebond', 'duree_moy', 'pages_visite'],
      metricsExpanded: ['visites', 'visiteurs', 'pages_vues', 'rebond', 'duree_moy', 'pages_visite', 'taux_conv', 'conv']
    }
  ],
  // Templates TikTok Ads (alignés sur Google Ads / Microsoft Ads)
  tiktok: [
    {
      id: 'ecommerce',
      name: 'E-commerce',
      description: 'Métriques orientées ventes et rentabilité',
      metrics: ['impressions', 'ctr', 'clics', 'cpc', 'cout', 'taux_conv', 'conv', 'panier_moyen', 'valeur_conv', 'cos'],
      metricsExpanded: ['impressions', 'ctr', 'clics', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'panier_moyen', 'valeur_conv', 'cos'],
      chartConfig: { barMetric: 'conversions', lineMetric: 'clicks' }
    },
    {
      id: 'leadgen',
      name: 'Lead Gen',
      description: 'Métriques orientées génération de leads',
      metrics: ['impressions', 'ctr', 'clics', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa'],
      metricsExpanded: ['impressions', 'ctr', 'clics', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa'],
      chartConfig: { barMetric: 'conversions', lineMetric: 'clicks' }
    }
  ],
  // Templates ChatGPT Ads — pas d'e-commerce : l'API OpenAI Ads ne renvoie pas la valeur
  // des conversions, donc ni COS, ni ROAS, ni panier moyen.
  chatgpt: [
    /**
     * Les mêmes onze colonnes que le template E-commerce de Microsoft Ads, dans le même
     * ordre : un compte marchand se juge aux mêmes chiffres quelle que soit la régie.
     *
     * Ce template n'existait pas — le dashboard tenait la valeur des conversions pour
     * inaccessible côté OpenAI. Elle l'est : `/conversions/insights` rend
     * `order_created_attributed_sales` aux trois niveaux d'agrégation.
     *
     * ROAS et ROI restent hors du template, comme chez Microsoft Ads : onze colonnes
     * tiennent déjà le plafond de douze, et les deux restent à portée d'un clic dans
     * « Métriques disponibles ».
     */
    {
      id: 'ecommerce',
      name: 'E-commerce',
      description: 'Métriques orientées ventes et rentabilité',
      metrics: ['impressions', 'ctr', 'clics', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'panier_moyen', 'valeur_conv', 'cos'],
      metricsExpanded: ['impressions', 'ctr', 'clics', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'panier_moyen', 'valeur_conv', 'cos'],
      // Le même couple que les cinq autres templates E-commerce du fichier.
      chartConfig: { barMetric: 'conversions', lineMetric: 'clicks' }
    },
    {
      id: 'leadgen',
      name: 'Lead Gen',
      description: 'Métriques orientées génération de leads',
      metrics: ['impressions', 'ctr', 'clics', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa'],
      metricsExpanded: ['impressions', 'ctr', 'clics', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa'],
      chartConfig: { barMetric: 'conversions', lineMetric: 'clicks' }
    },
    {
      id: 'notoriete',
      name: 'Notoriété',
      description: 'Métriques orientées diffusion et coût du contact',
      metrics: ['impressions', 'clics', 'ctr', 'cpc', 'cout'],
      metricsExpanded: ['impressions', 'clics', 'ctr', 'cpc', 'cout'],
      chartConfig: { barMetric: 'impressions', lineMetric: 'ctr' }
    }
  ],
  /**
   * Awin suit les deux mêmes intentions que les autres régies — vendre, ou capter des
   * contacts — et porte donc les mêmes noms, pour qu'un utilisateur retrouve ses repères d'un
   * dashboard à l'autre.
   *
   * Deux métriques manquent partout : **les impressions**, qu'Awin ne mesure que sur les
   * emplacements display et qui valent zéro chez la quasi-totalité des éditeurs, et le **CTR**
   * qui en découle. Les proposer inviterait à cocher des colonnes condamnées à rester vides.
   */
  awin: [
    {
      id: 'ecommerce',
      name: 'E-commerce',
      description: 'Métriques orientées ventes et rentabilité',
      metrics: ['clics', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'panier_moyen', 'valeur_conv', 'cos'],
      metricsExpanded: ['clics', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa', 'panier_moyen', 'valeur_conv', 'cos'],
      chartConfig: { barMetric: 'conversions', lineMetric: 'clicks' }
    },
    {
      id: 'leadgen',
      name: 'Lead Gen',
      description: 'Métriques orientées génération de leads',
      metrics: ['clics', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa'],
      metricsExpanded: ['clics', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa'],
      chartConfig: { barMetric: 'conversions', lineMetric: 'clicks' }
    }
  ],
  // Templates Pinterest Ads
  pinterest: [
    {
      id: 'ecommerce',
      name: 'E-commerce',
      description: 'Métriques orientées ventes et rentabilité',
      metrics: ['impressions', 'ctr', 'clics', 'cpc', 'cout', 'taux_conv', 'conv', 'panier_moyen', 'valeur_conv', 'cos'],
      metricsExpanded: ['impressions', 'ctr', 'clics', 'cpc', 'cout', 'taux_conv', 'conv', 'panier_moyen', 'valeur_conv', 'cos'],
      chartConfig: { barMetric: 'conversions', lineMetric: 'clicks' }
    },
    {
      id: 'leadgen',
      name: 'Lead Gen',
      description: 'Métriques orientées génération de leads',
      metrics: ['impressions', 'ctr', 'clics', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa'],
      metricsExpanded: ['impressions', 'ctr', 'clics', 'cpc', 'cout', 'taux_conv', 'conv', 'cpa'],
      chartConfig: { barMetric: 'conversions', lineMetric: 'clicks' }
    }
  ]
};

// Liste des composants par plateforme pour l'application globale des templates
export const PLATFORM_COMPONENTS = {
  gads: ['results', 'comparatif', 'campaigns', 'ad_groups', 'asset_groups', 'devices', 'weekdays', 'geography', 'networks', 'campaign_types', 'age_ranges', 'gender', 'audiences', 'keywords', 'keywords_cpc_tiers', 'keywords_match_type', 'keywords_quality_score', 'search_terms', 'search_terms_combined', 'search_terms_cpc_tiers', 'search_terms_ngram', 'landing_pages', 'shopping_products', 'pmax', 'topflop_terms_top', 'topflop_terms_flop', 'topflop_products_top', 'topflop_products_flop', 'topflop_categories_top', 'topflop_categories_flop', 'topflop_itemgroups_top', 'topflop_itemgroups_flop'],
  msads: ['msads_results', 'msads_campaigns', 'msads_adgroups', 'msads_campaign_types', 'msads_devices', 'msads_networks', 'msads_search_terms', 'msads_geography', 'msads_weekdays'],
  fbads: ['fbads_results', 'fbads_campaigns', 'fbads_devices', 'fbads_weekdays', 'fbads_age', 'fbads_gender', 'fbads_placements', 'fbads_platform', 'fbads_adsets', 'fbads_ads', 'fbads_engagement'],
  ga4: ['ga4_results', 'ga4_channels', 'ga4_geography', 'ga4_devices', 'ga4_pages', 'ga4_weekdays'],
  piano: ['piano_results', 'piano_channels', 'piano_geography', 'piano_devices', 'piano_browsers', 'piano_languages', 'piano_pages', 'piano_weekdays'],
  matomo: ['matomo_results', 'matomo_channels', 'matomo_geography', 'matomo_devices', 'matomo_browsers', 'matomo_languages', 'matomo_weekdays', 'matomo_landing_pages', 'matomo_ecommerce_products', 'matomo_events'],
  tiktok: ['tiktok_results', 'tiktok_campaigns'],
  pinterest: ['pinterest_results'],
  chatgpt: ['chatgpt_results', 'chatgpt_campaigns', 'chatgpt_ad_groups', 'chatgpt_ads'],
  awin: ['awin_results', 'awin_publishers', 'awin_campaigns']
};

// Applique un template à tous les composants d'une plateforme
// IMPORTANT: N'applique qu'au compte spécifié (accountId), pas à tous les comptes
/**
 * Les composants dont le vocabulaire n'a rien à voir avec celui des modèles.
 *
 * **Un modèle parle de sessions, de rebond et de ventes.** Le tableau des événements compte des
 * événements et des événements par visite : l'intersection se réduit à « Sessions » et
 * « Utilisateurs », et appliquer n'importe lequel des trois modèles lui retirait ses deux
 * colonnes propres — celles qui lui donnent son sens.
 *
 * Ces composants gardent donc leurs métriques, quel que soit le modèle retenu. Leur
 * configuration reste modifiable un par un, par la modale.
 */
const COMPOSANTS_HORS_TEMPLATE = new Set(['matomo_events']);

export const applyTemplateToAllComponents = (template, accountId = 'global', platform = 'gads') => {
  const components = PLATFORM_COMPONENTS[platform] || PLATFORM_COMPONENTS.gads;

  // Charger les données existantes une seule fois
  let all = {};
  try {
    const existing = localStorage.getItem('kokpeet_component_metrics');
    if (existing) {
      all = JSON.parse(existing);
    }
  } catch (e) {
    console.error('Erreur lecture localStorage:', e);
  }

  // Appliquer UNIQUEMENT au compte spécifié (pas à tous les comptes)
  const targetAccountId = accountId || 'global';

  console.log(`💾 [TEMPLATE] Application "${template.name}" à ${components.length} composants (${platform}) pour le compte ${targetAccountId}`);

  if (!all[targetAccountId]) all[targetAccountId] = {};

  components.forEach(compId => {
    if (COMPOSANTS_HORS_TEMPLATE.has(compId)) return;

    const availableMetrics = COMPONENT_AVAILABLE_METRICS[compId] || [];
    const limit = COMPONENT_METRICS_LIMITS[compId] || 10;

    // Choisir les métriques selon le type de composant
    let templateMetrics;
    if (compId.startsWith('topflop_')) {
      // Composants Top/Flop : utiliser les métriques par défaut spécifiques
      templateMetrics = COMPONENT_DEFAULT_METRICS[compId] || [];
    } else if (compId === 'results' || compId.endsWith('_results')) {
      // Composant Résultats : version courte
      templateMetrics = template.metrics;
    } else {
      // Autres composants : version longue (ou courte si pas de metricsExpanded)
      templateMetrics = template.metricsExpanded || template.metrics;
    }

    // Filtrer pour ne garder que les métriques disponibles pour ce composant
    const filteredMetrics = templateMetrics.filter(m => availableMetrics.includes(m));
    // Limiter au nombre max
    const limitedMetrics = filteredMetrics.slice(0, limit);

    // Préparer les données du composant
    const compData = {
      metrics: limitedMetrics,
      order: limitedMetrics,
      labels: {}
    };

    /**
     * `chartConfig` ne concerne que les composants Résultats — eux seuls portent un graphe.
     *
     * L'horodatage n'est pas décoratif : le graphe ne réapplique la configuration que si elle
     * a changé, en comparant sa forme sérialisée. Sans lui, réappliquer le même template après
     * avoir choisi une autre courbe à la main ne reprenait pas la main — la configuration était
     * identique, donc jugée déjà appliquée, et le graphe gardait le choix manuel. Or demander
     * un template, c'est demander qu'il s'applique.
     */
    if ((compId === 'results' || compId.endsWith('_results')) && template.chartConfig) {
      compData.chartConfig = { ...template.chartConfig, appliqueLe: Date.now() };
    }

    // Stocker dans l'objet all pour ce compte uniquement
    all[targetAccountId][compId] = compData;
  });

  /**
   * Le template retenu, mémorisé à côté des composants qu'il vient de régler.
   *
   * Jusqu'ici seul son **effet** était conservé — les listes de métriques — et non le choix
   * lui-même. Impossible, dès lors, de faire dépendre quoi que ce soit du template : c'est
   * pourtant ce qu'appelle le dashboard Google Analytics, où le parcours d'achat et les achats
   * e-commerce n'ont de sens que sous « E-commerce ».
   *
   * Rangé **par plateforme** : un même compte peut porter des composants de familles
   * différentes, et un template Google Ads n'a rien à dire du template Analytics.
   *
   * ⚠️ `__template` cohabite avec les composants dans l'objet du compte. C'est déjà le cas de
   * `CLE_VERSION` au niveau au-dessus, et les lecteurs vont chercher un composant **par son
   * nom** — jamais en parcourant les clés. Une clé de plus ne peut donc pas être prise pour
   * un composant.
   */
  if (!all[targetAccountId].__template) all[targetAccountId].__template = {};
  all[targetAccountId].__template[platform] = template.id;

  console.log(`  📝 Compte ${targetAccountId}: ${components.length} composants mis à jour`);

  // Sauvegarder tout d'un coup
  try {
    localStorage.setItem('kokpeet_component_metrics', JSON.stringify(all));
    // Dispatcher l'événement une seule fois à la fin
    window.dispatchEvent(new Event('componentMetricsChanged'));
    console.log(`💾 [TEMPLATE] ✅ Sauvegardé "${template.name}" pour ${components.length} composants (compte: ${targetAccountId})`);

    // Synchroniser vers l'API en arrière-plan (pour persistance organisation)
    saveToApi(all);
  } catch (e) {
    console.error('💾 [TEMPLATE] ❌ Erreur sauvegarde:', e);
  }
};

// Récupère les templates disponibles pour un composant
export const getTemplatesForComponent = (componentId) => {
  // Facebook Ads
  if (componentId.startsWith('fbads_')) return METRICS_TEMPLATES.fbads;
  // Microsoft Ads
  if (componentId.startsWith('msads_')) return METRICS_TEMPLATES.msads;
  // TikTok Ads
  if (componentId.startsWith('tiktok_')) return METRICS_TEMPLATES.tiktok;
  // Pinterest Ads
  if (componentId.startsWith('pinterest_')) return METRICS_TEMPLATES.pinterest;
  if (componentId.startsWith('chatgpt_')) return METRICS_TEMPLATES.chatgpt;
  if (componentId.startsWith('awin_')) return METRICS_TEMPLATES.awin;
  // Analytics (GA4, Piano, Matomo)
  if (componentId.startsWith('ga4_')) return METRICS_TEMPLATES.analytics;
  if (componentId.startsWith('piano_')) return METRICS_TEMPLATES.analytics;
  if (componentId.startsWith('matomo_')) return METRICS_TEMPLATES.analytics;
  // Défaut: Google Ads
  return METRICS_TEMPLATES.gads;
};

// ===== MÉTRIQUES AVEC OPTIONS AVANCÉES (SAUVEGARDE SERVEUR) =====
// Configuration des métriques qui ont des options avancées nécessitant une sauvegarde côté serveur
export const METRICS_WITH_ADVANCED_OPTIONS = {
  // Google Analytics 4 - Résultats
  ga4_results: {
    conv: {
      type: 'conversion_config',
      label: 'Configuration des conversions',
      description: 'Choisir l\'événement à compter comme conversion',
      requiresServerSave: true,
      apiEndpoint: '/api/dashboard/ga4/conversion-settings',
      loadEndpoint: '/api/dashboard/ga4/available-events'
    }
  },
  // Google Analytics 4 - Canaux
  ga4_channels: {
    conv: {
      type: 'conversion_config',
      label: 'Configuration des conversions',
      description: 'Choisir l\'événement à compter comme conversion',
      requiresServerSave: true,
      apiEndpoint: '/api/dashboard/ga4/conversion-settings',
      loadEndpoint: '/api/dashboard/ga4/available-events'
    }
  },
  // Google Analytics 4 - Géographie
  ga4_geography: {
    conv: {
      type: 'conversion_config',
      label: 'Configuration des conversions',
      description: 'Choisir l\'événement à compter comme conversion',
      requiresServerSave: true,
      apiEndpoint: '/api/dashboard/ga4/conversion-settings',
      loadEndpoint: '/api/dashboard/ga4/available-events'
    }
  },
  // Google Analytics 4 - Appareils & Navigateurs
  ga4_devices: {
    conv: {
      type: 'conversion_config',
      label: 'Configuration des conversions',
      description: 'Choisir l\'événement à compter comme conversion',
      requiresServerSave: true,
      apiEndpoint: '/api/dashboard/ga4/conversion-settings',
      loadEndpoint: '/api/dashboard/ga4/available-events'
    }
  },
  // Google Analytics 4 - Pages d'atterrissage
  ga4_pages: {
    conv: {
      type: 'conversion_config',
      label: 'Configuration des conversions',
      description: 'Choisir l\'événement à compter comme conversion',
      requiresServerSave: true,
      apiEndpoint: '/api/dashboard/ga4/conversion-settings',
      loadEndpoint: '/api/dashboard/ga4/available-events'
    }
  },
  // Google Analytics 4 - Jours de la semaine
  ga4_weekdays: {
    conv: {
      type: 'conversion_config',
      label: 'Configuration des conversions',
      description: 'Choisir l\'événement à compter comme conversion',
      requiresServerSave: true,
      apiEndpoint: '/api/dashboard/ga4/conversion-settings',
      loadEndpoint: '/api/dashboard/ga4/available-events'
    }
  },
  /**
   * Piano Analytics — les événements que le site enregistre.
   *
   * Les chemins sont ceux de GA4 au nom de la plateforme près, et les routes rendent les mêmes
   * clés : la modale de configuration est la même pour les trois, et n'a pas à savoir laquelle
   * lui répond.
   */
  piano_results: {
    conv: {
      type: 'conversion_config',
      label: 'Configuration des conversions',
      description: 'Choisir l\'événement à compter comme conversion',
      requiresServerSave: true,
      apiEndpoint: '/api/dashboard/piano/conversion-settings',
      loadEndpoint: '/api/dashboard/piano/available-events'
    }
  },
  /**
   * Meta Ads — ses **types d'actions**, conversions personnalisées comprises.
   *
   * Les chemins suivent la même convention que les trois autres plateformes, et la route rend
   * les mêmes clés : la modale n'a pas à savoir qui lui répond.
   *
   * ⚠️ **Tous les écrans Meta sont déclarés, pas seulement Résultats.** La définition est
   * stockée sur le compte et s'applique partout : un seul point de réglage aurait obligé à
   * revenir sur Résultats pour comprendre ce que compte la colonne d'un tableau de genre.
   */
  fbads_results: {
    conv: {
      type: 'conversion_config',
      label: 'Configuration des conversions',
      description: 'Choisir le ou les types de conversion à compter',
      requiresServerSave: true,
      apiEndpoint: '/api/dashboard/meta/conversion-settings',
      loadEndpoint: '/api/dashboard/meta/available-events'
    }
  },
  fbads_campaigns: {
    conv: {
      type: 'conversion_config',
      label: 'Configuration des conversions',
      description: 'Choisir le ou les types de conversion à compter',
      requiresServerSave: true,
      apiEndpoint: '/api/dashboard/meta/conversion-settings',
      loadEndpoint: '/api/dashboard/meta/available-events'
    }
  },
  fbads_platform: {
    conv: {
      type: 'conversion_config',
      label: 'Configuration des conversions',
      description: 'Choisir le ou les types de conversion à compter',
      requiresServerSave: true,
      apiEndpoint: '/api/dashboard/meta/conversion-settings',
      loadEndpoint: '/api/dashboard/meta/available-events'
    }
  },
  fbads_adsets: {
    conv: {
      type: 'conversion_config',
      label: 'Configuration des conversions',
      description: 'Choisir le ou les types de conversion à compter',
      requiresServerSave: true,
      apiEndpoint: '/api/dashboard/meta/conversion-settings',
      loadEndpoint: '/api/dashboard/meta/available-events'
    }
  },
  fbads_ads: {
    conv: {
      type: 'conversion_config',
      label: 'Configuration des conversions',
      description: 'Choisir le ou les types de conversion à compter',
      requiresServerSave: true,
      apiEndpoint: '/api/dashboard/meta/conversion-settings',
      loadEndpoint: '/api/dashboard/meta/available-events'
    }
  },
  fbads_devices: {
    conv: {
      type: 'conversion_config',
      label: 'Configuration des conversions',
      description: 'Choisir le ou les types de conversion à compter',
      requiresServerSave: true,
      apiEndpoint: '/api/dashboard/meta/conversion-settings',
      loadEndpoint: '/api/dashboard/meta/available-events'
    }
  },
  fbads_weekdays: {
    conv: {
      type: 'conversion_config',
      label: 'Configuration des conversions',
      description: 'Choisir le ou les types de conversion à compter',
      requiresServerSave: true,
      apiEndpoint: '/api/dashboard/meta/conversion-settings',
      loadEndpoint: '/api/dashboard/meta/available-events'
    }
  },
  fbads_age: {
    conv: {
      type: 'conversion_config',
      label: 'Configuration des conversions',
      description: 'Choisir le ou les types de conversion à compter',
      requiresServerSave: true,
      apiEndpoint: '/api/dashboard/meta/conversion-settings',
      loadEndpoint: '/api/dashboard/meta/available-events'
    }
  },
  fbads_gender: {
    conv: {
      type: 'conversion_config',
      label: 'Configuration des conversions',
      description: 'Choisir le ou les types de conversion à compter',
      requiresServerSave: true,
      apiEndpoint: '/api/dashboard/meta/conversion-settings',
      loadEndpoint: '/api/dashboard/meta/available-events'
    }
  },
  fbads_placements: {
    conv: {
      type: 'conversion_config',
      label: 'Configuration des conversions',
      description: 'Choisir le ou les types de conversion à compter',
      requiresServerSave: true,
      apiEndpoint: '/api/dashboard/meta/conversion-settings',
      loadEndpoint: '/api/dashboard/meta/available-events'
    }
  },
  /*
   * ⚠️ **`fbads_engagement` n'est pas ici, et ce n'est pas un oubli.**
   *
   * `EngagementDetailsTable` ne rend que trois colonnes — Engagements, Dépenses,
   * Coût/Engagement — et n'a aucune cellule pour les conversions. Y poser l'engrenage
   * ouvrirait un réglage dont cet écran ne montrerait jamais l'effet.
   *
   * Son entrée dans `COMPONENT_AVAILABLE_METRICS` propose pourtant `conv`, `taux_conv` et une
   * quinzaine d'autres que ce tableau ne sait pas afficher : défaut antérieur, laissé tel
   * quel — le corriger demande d'ajouter des colonnes, ce qui est une autre décision.
   */
  /**
   * Matomo — ses **objectifs** tiennent le rôle des événements clés.
   *
   * `Goals.getGoals` les liste, `Goals.get&idGoal=N` les dénombre. Ils sont stockés sous la
   * forme `goal:<id>` dans le même champ `conversionEvents` que les événements des deux
   * autres : un entier seul se confondrait avec un nom d'événement.
   */
  matomo_results: {
    conv: {
      type: 'conversion_config',
      label: 'Configuration des conversions',
      description: 'Choisir l\'objectif à compter comme conversion',
      requiresServerSave: true,
      apiEndpoint: '/api/dashboard/matomo/conversion-settings',
      loadEndpoint: '/api/dashboard/matomo/available-events'
    }
  }
};

// Récupère les options avancées d'une métrique pour un composant donné
export const getMetricAdvancedOptions = (componentId, metricKey) => {
  return METRICS_WITH_ADVANCED_OPTIONS[componentId]?.[metricKey] || null;
};

// Vérifie si un composant a des métriques avec options avancées
export const hasAdvancedOptions = (componentId) => {
  return !!METRICS_WITH_ADVANCED_OPTIONS[componentId];
};

// Récupère toutes les métriques avec options avancées pour un composant
export const getAdvancedOptionsForComponent = (componentId) => {
  return METRICS_WITH_ADVANCED_OPTIONS[componentId] || {};
};

// ===== MAPPING CLÉS INTERNES VERS CLÉS API =====
// Permet d'afficher les vraies clés API utilisées par les plateformes

// Google Ads API
export const GOOGLE_ADS_API_KEYS = {
  clics: 'metrics.clicks',
  impressions: 'metrics.impressions',
  ctr: 'metrics.ctr',
  cpc: 'metrics.average_cpc',
  cout: 'metrics.cost_micros',
  taux_conv: 'metrics.conversions / metrics.clicks',
  conv: 'metrics.conversions',
  cpa: 'metrics.cost_per_conversion',
  valeur_conv: 'metrics.conversions_value',
  cos: 'cost / conversions_value',
  panier_moyen: 'conversions_value / conversions',
  roas: '(conversions_value / cost) × 100',
  roi: '(conversions_value - cost) / cost'
};

// Google Analytics 4 Data API
export const GA4_API_KEYS = {
  visites: 'sessions',
  visiteurs: 'activeUsers',
  pages_vues: 'screenPageViews',
  rebond: 'bounceRate',
  duree_moy: 'averageSessionDuration',
  pages_visite: 'screenPageViewsPerSession',
  conv: 'conversions', // métrique GA4 conversions
  taux_conv: 'conversions / sessions',
  valeur_conv: 'totalRevenue',
  transactions: 'transactions',
  chiffre_affaires: 'totalRevenue',
  taux_transaction: 'transactions / sessions'
};

// Piano Analytics API
export const PIANO_API_KEYS = {
  visites: 'm_visits',
  visiteurs: 'm_visitors',
  pages_vues: 'm_page_loads',
  rebond: 'm_bounce_rate',
  duree_moy: 'm_time_spent_per_visits',
  conv: 'm_goal_conversions',
  taux_conv: 'm_goal_conversion_rate',
  valeur_conv: 'm_goal_revenue'
};

// Matomo API
export const MATOMO_API_KEYS = {
  visites: 'nb_visits',
  visiteurs: 'nb_uniq_visitors',
  pages_vues: 'nb_pageviews',
  rebond: 'bounce_rate',
  duree_moy: 'avg_time_on_site',
  pages_visite: 'nb_actions_per_visit',
  conv: 'nb_conversions',
  taux_conv: 'conversion_rate',
  valeur_conv: 'revenue',
  events: 'nb_events',
  actions_par_event: 'avg_event_actions'
};

// Facebook Ads API
export const FACEBOOK_ADS_API_KEYS = {
  clics: 'clicks',
  impressions: 'impressions',
  couverture: 'reach',
  repetition: 'frequency',
  ctr: 'ctr',
  cpc: 'cpc',
  cout: 'spend',
  conv: 'actions[purchase]',
  valeur_conv: 'action_values[purchase]',
  interactions: 'actions',
  reactions: 'reactions'
};

// Mapping générique (fallback)
export const METRIC_API_KEYS = {
  ...GOOGLE_ADS_API_KEYS,
  ...GA4_API_KEYS
};

// Récupère la clé API pour une métrique selon le type de composant
export const getMetricApiKey = (metricKey, componentId = null) => {
  // Déterminer le type de plateforme selon le componentId
  if (componentId) {
    if (componentId.startsWith('ga4_')) {
      return GA4_API_KEYS[metricKey] || metricKey;
    }
    if (componentId.startsWith('piano_')) {
      return PIANO_API_KEYS[metricKey] || metricKey;
    }
    if (componentId.startsWith('matomo_')) {
      return MATOMO_API_KEYS[metricKey] || metricKey;
    }
    if (componentId.startsWith('fbads_')) {
      return FACEBOOK_ADS_API_KEYS[metricKey] || metricKey;
    }
  }
  // Fallback sur Google Ads
  return GOOGLE_ADS_API_KEYS[metricKey] || metricKey;
};

// Récupère la configuration des métriques d'un composant spécifique
/**
 * Le réglage de graphique que le modèle actif impose, à défaut de réglage enregistré.
 *
 * **Pourquoi ce repli existe.** `chartConfig` — quelle métrique en barres, laquelle en courbe —
 * vivait dans la seule entrée du composant, et plusieurs chemins la reconstruisaient de zéro :
 * elle disparaissait sans un mot, et le graphe revenait à ses valeurs par défaut au
 * rechargement. Les correctifs en amont ont bouché ces fuites, mais ils ne réparent pas les
 * configurations déjà vidées — un utilisateur aurait dû réappliquer son modèle pour comprendre.
 *
 * Or l'intention est toujours là : le modèle retenu est mémorisé à part (`__template`), et c'est
 * lui qui porte le couple. On le redéduit donc plutôt que de l'exiger deux fois.
 *
 * Ne vaut que pour les composants Résultats : eux seuls portent un graphe.
 */
const chartConfigDuTemplate = (componentId, accountId) => {
  if (!(componentId === 'results' || componentId.endsWith('_results'))) return null;

  try {
    const plateforme = Object.entries(PLATFORM_COMPONENTS)
      .find(([, composants]) => composants.includes(componentId))?.[0];
    if (!plateforme) return null;

    /*
     * La lecture est faite ici plutôt que par `getAppliedTemplate`, déclaré plus bas dans le
     * fichier : la dépendance tiendrait à l'ordre d'évaluation du module, et c'est exactement
     * le genre de fil qu'on casse sans s'en apercevoir.
     */
    const brut = localStorage.getItem('kokpeet_component_metrics');
    if (!brut) return null;
    const stocke = JSON.parse(brut);
    const id = stocke?.[accountId]?.__template?.[plateforme]
      || stocke?.global?.__template?.[plateforme];
    if (!id) return null;

    return getTemplatesForComponent(componentId).find((t) => t.id === id)?.chartConfig || null;
  } catch (e) {
    return null;
  }
};

export const getComponentMetrics = (componentId, accountId = 'global') => {
  // Métriques disponibles pour ce composant
  const availableMetrics = COMPONENT_AVAILABLE_METRICS[componentId] || [];

  try {
    const saved = localStorage.getItem('kokpeet_component_metrics');
    if (saved) {
      const parsed = JSON.parse(saved);
      let config = null;

      // Chercher d'abord pour le compte spécifique
      if (parsed[accountId]?.[componentId]) {
        config = parsed[accountId][componentId];
      }
      // Sinon utiliser les paramètres globaux
      else if (parsed.global?.[componentId]) {
        config = parsed.global[componentId];
      }

      if (config) {
        // Filtrer les métriques sauvegardées pour ne garder que celles disponibles
        const filteredMetrics = (config.metrics || []).filter(m => availableMetrics.includes(m));
        const filteredOrder = (config.order || config.metrics || []).filter(m => availableMetrics.includes(m));

        return {
          ...config,
          metrics: filteredMetrics,
          order: filteredOrder,
          // Une configuration enregistrée avant l'existence du graphique n'en porte pas :
          // le défaut du composant reprend la main plutôt que le repli générique.
          chartConfig: config.chartConfig
            || chartConfigDuTemplate(componentId, accountId)
            || COMPONENT_DEFAULT_CHART_CONFIG[componentId]
            || null
        };
      }
    }
  } catch (e) {
    console.error('Erreur lecture métriques composant:', e);
  }
  // Retourner les valeurs par défaut
  const defaultMetrics = COMPONENT_DEFAULT_METRICS[componentId] || [];
  return {
    metrics: defaultMetrics,
    order: defaultMetrics,
    labels: {}, // Labels personnalisés par métrique
    chartConfig: chartConfigDuTemplate(componentId, accountId)
      || COMPONENT_DEFAULT_CHART_CONFIG[componentId]
      || null
  };
};

/**
 * Mémorise le réglage du graphique d'un composant Résultats.
 *
 * **Pourquoi il manquait.** Le couple barres/courbe n'était écrit que par l'application d'un
 * modèle. Un choix fait à la main — cliquer sur une carte pour la passer en barres — ne vivait
 * que dans l'état React : au rechargement suivant, le graphe repartait sur ses valeurs par
 * défaut. L'utilisateur voyait « son » réglage sauter sans rien avoir fait.
 *
 * On écrit donc dans la même entrée que les métriques, en préservant tout le reste.
 *
 * `appliqueLe` n'est pas décoratif : le composant ne réapplique une configuration que si elle a
 * changé, en comparant sa forme sérialisée. Sans horodatage, repasser deux fois par le même
 * couple ne reprendrait pas la main.
 */
export const saveChartConfig = (componentId, chartConfig, accountId = 'global') => {
  if (!componentId || !chartConfig) return;

  try {
    const existant = localStorage.getItem('kokpeet_component_metrics');
    const all = existant ? JSON.parse(existant) : {};
    const compte = accountId || 'global';

    if (!all[compte]) all[compte] = {};
    const precedent = all[compte][componentId] || {};

    // Rien à écrire si le couple est déjà celui enregistré : on éviterait surtout d'empiler
    // des écritures à chaque rendu.
    const ancien = precedent.chartConfig || {};
    if (ancien.barMetric === chartConfig.barMetric && ancien.lineMetric === chartConfig.lineMetric) {
      return;
    }

    all[compte][componentId] = {
      ...precedent,
      chartConfig: { ...chartConfig, appliqueLe: Date.now() }
    };

    localStorage.setItem('kokpeet_component_metrics', JSON.stringify(all));
    window.dispatchEvent(new Event('componentMetricsChanged'));
    saveToApi(all);
  } catch (e) {
    console.error('Erreur enregistrement du réglage de graphique:', e);
  }
};

// Sauvegarde les métriques d'un composant avec option "appliquer à tous"
export const saveComponentMetrics = (componentId, metrics, order, accountId = 'global', applyToAll = false, labels = {}, chartConfig = null) => {
  try {
    let all = {};
    const existing = localStorage.getItem('kokpeet_component_metrics');
    if (existing) {
      all = JSON.parse(existing);
    }

    /*
     * **L'entrée était reconstruite de zéro, ce qui effaçait le réglage du graphique.**
     *
     * `chartConfig` — quelle métrique en barres, laquelle en courbe — n'est passé que lorsqu'un
     * modèle l'impose. Enregistrer des métriques depuis la modale repartait donc d'un objet
     * neuf : le couple choisi disparaissait sans un mot, et le graphe revenait à ses valeurs
     * par défaut au rechargement suivant. L'utilisateur voyait son réglage « sauter » après
     * avoir touché à tout autre chose.
     *
     * On repart donc de l'existant, et `chartConfig` n'est écrasé que s'il est fourni.
     */
    const precedent = all[accountId || 'global']?.[componentId] || {};
    const componentData = { ...precedent, metrics, order, labels };
    if (chartConfig) {
      componentData.chartConfig = chartConfig;
    }

    if (applyToAll) {
      // Déterminer la plateforme du composant actuel en utilisant PLATFORM_COMPONENTS
      let platform = 'gads'; // Par défaut Google Ads
      for (const [plat, components] of Object.entries(PLATFORM_COMPONENTS)) {
        if (components.includes(componentId)) {
          platform = plat;
          break;
        }
      }

      // Récupérer tous les composants de cette plateforme
      const platformComponents = PLATFORM_COMPONENTS[platform] || PLATFORM_COMPONENTS.gads;

      console.log(`💾 [SAVE-ALL] Plateforme détectée: ${platform}, composants: ${platformComponents.length}`);

      // Appliquer à tous les composants de la même plateforme (en respectant les limites)
      platformComponents.forEach(compId => {

        if (!all[accountId]) all[accountId] = {};
        const limit = COMPONENT_METRICS_LIMITS[compId];
        const availableForComp = COMPONENT_AVAILABLE_METRICS[compId] || [];
        // Filtrer pour ne garder que les métriques disponibles pour ce composant
        const filteredMetrics = metrics.filter(m => availableForComp.includes(m)).slice(0, limit);
        const filteredOrder = order.filter(m => availableForComp.includes(m)).slice(0, limit);
        // Filtrer les labels pour ne garder que ceux des métriques disponibles
        const filteredLabels = {};
        Object.keys(labels).forEach(key => {
          if (availableForComp.includes(key)) {
            filteredLabels[key] = labels[key];
          }
        });
        // Même règle qu'au-dessus : on conserve ce que l'entrée portait déjà.
        const compData = {
          ...(all[accountId]?.[compId] || {}),
          metrics: filteredMetrics,
          order: filteredOrder,
          labels: filteredLabels
        };
        if (chartConfig && (compId === 'results' || compId.endsWith('_results'))) {
          compData.chartConfig = chartConfig;
        }
        all[accountId][compId] = compData;
      });
    } else {
      if (!all[accountId]) all[accountId] = {};
      all[accountId][componentId] = componentData;
    }

    localStorage.setItem('kokpeet_component_metrics', JSON.stringify(all));
    window.dispatchEvent(new Event('componentMetricsChanged'));
    console.log(`💾 [COMPONENT-METRICS] Sauvegardé pour ${componentId} (compte: ${accountId}, applyToAll: ${applyToAll})`);

    // Sauvegarder aussi vers l'API en arrière-plan
    saveToApi(all);

    return true;
  } catch (e) {
    console.error('Erreur sauvegarde métriques composant:', e);
    return false;
  }
};

// Hook pour écouter les changements de métriques par composant
export const useComponentMetrics = (componentId, accountId = 'global') => {
  const [config, setConfig] = useState(() => getComponentMetrics(componentId, accountId));

  // Recharger la config quand componentId ou accountId changent
  useEffect(() => {
    const newConfig = getComponentMetrics(componentId, accountId);
    console.log(`🔄 [METRICS-HOOK] ${componentId} (account: ${accountId}) chargement initial:`, newConfig.metrics.join(', '));
    setConfig(newConfig);
  }, [componentId, accountId]);

  useEffect(() => {
    const handleChange = () => {
      const newConfig = getComponentMetrics(componentId, accountId);
      console.log(`🔄 [METRICS-HOOK] ${componentId} (account: ${accountId}) reçoit:`, newConfig.metrics.join(', '));
      setConfig(newConfig);
    };
    window.addEventListener('storage', handleChange);
    window.addEventListener('componentMetricsChanged', handleChange);
    return () => {
      window.removeEventListener('storage', handleChange);
      window.removeEventListener('componentMetricsChanged', handleChange);
    };
  }, [componentId, accountId]);

  return config;
};

/**
 * Le template de métriques retenu pour un compte, ou `null` si aucun n'a été appliqué.
 *
 * `null` n'est pas « aucun template » au sens d'un template vide : c'est « la question n'a
 * jamais été posée ». Les écrans qui s'en servent doivent traiter ce cas comme l'ancien
 * comportement, et non comme un refus — sans quoi la mise en ligne retirerait des composants
 * à tous ceux qui n'ont jamais ouvert le sélecteur de template.
 */
export const getAppliedTemplate = (accountId = 'global', platform = 'gads') => {
  try {
    const saved = localStorage.getItem('kokpeet_component_metrics');
    if (!saved) return null;
    const parsed = JSON.parse(saved);
    return parsed?.[accountId]?.__template?.[platform]
      // Le compte n'a rien reçu en propre : le réglage global fait foi, comme pour les
      // métriques elles-mêmes.
      || parsed?.global?.__template?.[platform]
      || null;
  } catch (e) {
    console.error('Erreur lecture du template appliqué:', e);
    return null;
  }
};

/** Même lecture, tenue à jour quand un template est appliqué sans recharger la page. */
export const useAppliedTemplate = (accountId = 'global', platform = 'gads') => {
  const [template, setTemplate] = useState(() => getAppliedTemplate(accountId, platform));

  useEffect(() => {
    const relire = () => setTemplate(getAppliedTemplate(accountId, platform));
    relire();
    window.addEventListener('storage', relire);
    window.addEventListener('componentMetricsChanged', relire);
    return () => {
      window.removeEventListener('storage', relire);
      window.removeEventListener('componentMetricsChanged', relire);
    };
  }, [accountId, platform]);

  return template;
};

// Vérifie si une métrique est sélectionnée pour un composant spécifique
export const isComponentMetricSelected = (componentId, metricKey, accountId = 'global') => {
  const config = getComponentMetrics(componentId, accountId);
  return config.metrics.includes(metricKey);
};

// ===== GESTION DE LA VISIBILITÉ DES MODULES =====

// Liste des modules disponibles avec leurs IDs
export const DASHBOARD_MODULES = [
  'results',           // Résultats (KPI Cards)
  'chart',             // Graphique
  'campaigns',         // Campagnes
  'campaign_types',    // Types de campagnes
  'ad_groups',         // Groupes d'annonces
  'asset_groups',      // Groupes Performance Max
  'networks',          // Réseaux
  'devices',           // Appareils
  'weekdays',          // Jours de la semaine
  'geography',         // Géographie
  'age_ranges',        // Tranches d'âge
  'gender',            // Genre
  'audiences',         // Audiences
  'topflop_terms',     // Top & Flop Termes
  'topflop_products',  // Top & Flop Produits
  'topflop_categories', // Top & Flop Catégories
  'topflop_itemgroups' // Top & Flop Groupes Produits
];

// Récupère la visibilité d'un module pour un compte spécifique
export const isModuleVisible = (moduleId, accountId = 'global') => {
  try {
    const saved = localStorage.getItem('kokpeet_module_visibility');
    if (saved) {
      const parsed = JSON.parse(saved);
      // Chercher d'abord pour le compte spécifique
      if (parsed[accountId]?.[moduleId] !== undefined) {
        return parsed[accountId][moduleId];
      }
      // Sinon utiliser les paramètres globaux
      if (parsed.global?.[moduleId] !== undefined) {
        return parsed.global[moduleId];
      }
    }
  } catch (e) {
    console.error('Erreur lecture visibilité module:', e);
  }
  // Par défaut, tous les modules sont visibles
  return true;
};

// Définit la visibilité d'un module pour un compte spécifique
export const setModuleVisibility = (moduleId, visible, accountId = 'global') => {
  try {
    let all = {};
    const existing = localStorage.getItem('kokpeet_module_visibility');
    if (existing) {
      all = JSON.parse(existing);
    }

    if (!all[accountId]) all[accountId] = {};
    all[accountId][moduleId] = visible;

    localStorage.setItem('kokpeet_module_visibility', JSON.stringify(all));
    window.dispatchEvent(new Event('moduleVisibilityChanged'));
    return true;
  } catch (e) {
    console.error('Erreur sauvegarde visibilité module:', e);
    return false;
  }
};

// Toggle la visibilité d'un module
export const toggleModuleVisibility = (moduleId, accountId = 'global') => {
  const currentVisibility = isModuleVisible(moduleId, accountId);
  return setModuleVisibility(moduleId, !currentVisibility, accountId);
};

// Récupère tous les modules masqués pour un compte
export const getHiddenModules = (accountId = 'global') => {
  return DASHBOARD_MODULES.filter(moduleId => !isModuleVisible(moduleId, accountId));
};

// Récupère tous les modules visibles pour un compte
export const getVisibleModules = (accountId = 'global') => {
  return DASHBOARD_MODULES.filter(moduleId => isModuleVisible(moduleId, accountId));
};

// Hook pour écouter les changements de visibilité des modules
export const useModuleVisibility = (moduleId, accountId = 'global') => {
  const [isVisible, setIsVisible] = useState(() => isModuleVisible(moduleId, accountId));

  useEffect(() => {
    const handleChange = () => {
      setIsVisible(isModuleVisible(moduleId, accountId));
    };
    window.addEventListener('storage', handleChange);
    window.addEventListener('moduleVisibilityChanged', handleChange);
    return () => {
      window.removeEventListener('storage', handleChange);
      window.removeEventListener('moduleVisibilityChanged', handleChange);
    };
  }, [moduleId, accountId]);

  const toggle = useCallback(() => {
    toggleModuleVisibility(moduleId, accountId);
  }, [moduleId, accountId]);

  const show = useCallback(() => {
    setModuleVisibility(moduleId, true, accountId);
  }, [moduleId, accountId]);

  const hide = useCallback(() => {
    setModuleVisibility(moduleId, false, accountId);
  }, [moduleId, accountId]);

  return { isVisible, toggle, show, hide };
};

// Hook pour récupérer tous les modules masqués
export const useHiddenModules = (accountId = 'global') => {
  const [hiddenModules, setHiddenModules] = useState(() => getHiddenModules(accountId));

  useEffect(() => {
    const handleChange = () => {
      setHiddenModules(getHiddenModules(accountId));
    };
    window.addEventListener('storage', handleChange);
    window.addEventListener('moduleVisibilityChanged', handleChange);
    return () => {
      window.removeEventListener('storage', handleChange);
      window.removeEventListener('moduleVisibilityChanged', handleChange);
    };
  }, [accountId]);

  return hiddenModules;
};